import {
  Accordion,
  Alert,
  Anchor,
  Badge,
  Card,
  Code,
  Divider,
  Group,
  List,
  SimpleGrid,
  Stack,
  Table,
  Text,
  ThemeIcon,
  Title,
  rem,
} from "@mantine/core";
import { createStyles } from "@mantine/emotion";
import {
  IconAlertCircle,
  IconApi,
  IconBrackets,
  IconCode,
  IconDatabase,
  IconInfoCircle,
  IconKey,
  IconListDetails,
  IconLock,
  IconPackage,
  IconPuzzle,
  IconRocket,
  IconSettings,
  IconSparkles,
  IconTerminal2,
  IconWebhook,
  IconWorldUpload,
} from "@tabler/icons-react";
import { graphql } from "gql.tada";
import { useQuery } from "@apollo/client/react";
import { ResultOf } from "@graphql-typed-document-node/core";
import { PageHeader } from "../../components/PageHeader";
import { useOrganizationLimits } from "../../../../business/limits.hook";
import { LimitUsageBar } from "../../components/LimitIndicator";
import {
  MCP_URL,
  LLMS_TXT_URL,
  LLMS_FULL_TXT_URL,
  SCHEMA_SDL_URL,
  CLAUDE_CODE_INSTALL_SNIPPET,
  ZERO_CLICK_SNIPPET,
  EXAMPLE_PROMPTS,
} from "./ai-devex-constants";

const useStyles = createStyles((theme) => ({
  section: {
    marginBottom: theme.spacing.xl,
  },
  sectionTitle: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing.sm,
    marginBottom: theme.spacing.md,
  },
  card: {
    backgroundColor: theme.colors.dark[7],
    border: `1px solid ${theme.colors.dark[5]}`,
  },
  codeBlock: {
    backgroundColor: theme.colors.dark[8],
    border: `1px solid ${theme.colors.dark[5]}`,
    borderRadius: theme.radius.md,
    padding: theme.spacing.md,
    fontFamily: "monospace",
    fontSize: theme.fontSizes.sm,
    lineHeight: 1.6,
    overflowX: "auto" as const,
    whiteSpace: "pre" as const,
    color: theme.colors.dark[0],
  },
  mutationBadge: {
    fontFamily: "monospace",
    textTransform: "none" as const,
    fontWeight: 600,
  },
  statusTable: {
    "& td, & th": {
      padding: `${rem(8)} ${rem(12)}`,
    },
  },
}));

const GqlBlock = ({ children }: { children: string }) => {
  const { classes } = useStyles();
  return <div className={classes.codeBlock}>{children.trim()}</div>;
};

const SectionHeader = ({
  icon,
  title,
  id,
}: {
  icon: React.ReactNode;
  title: React.ReactNode;
  id: string;
}) => {
  const { classes } = useStyles();
  return (
    <div className={classes.sectionTitle} id={id}>
      <ThemeIcon size="md" radius="md" variant="light" color="cyan">
        {icon}
      </ThemeIcon>
      <Title order={2}>{title}</Title>
    </div>
  );
};

const WebhookAvailableEventsQuery = graphql(`
  query webhookAvailableEvents {
    webhookAvailableEvents {
      type
      description
      answerPayload {
        type
        description
        optional
        properties {
          optional
          description
          type
          name
          array
          example
        }
      }
      payload {
        type
        description
        optional
        properties {
          optional
          description
          type
          name
          array
          example
        }
      }
    }
  }
`);

const createJsonPayloadExampleFromSchema = (
  schema:
    | ResultOf<
        typeof WebhookAvailableEventsQuery
      >["webhookAvailableEvents"][0]["payload"]
    | ResultOf<
        typeof WebhookAvailableEventsQuery
      >["webhookAvailableEvents"][0]["answerPayload"],
) => {
  const payload = schema?.properties?.reduce(
    (acc, property) => {
      acc[property.name] = property.example;
      return acc;
    },
    {} as Record<string, any>,
  );
  return JSON.stringify(payload, null, 2);
};

export const DeveloperDocumentation = () => {
  const { classes } = useStyles();
  const { data } = useQuery<any>(WebhookAvailableEventsQuery);

  const payloads = data?.webhookAvailableEvents.reduce(
    (acc: any, event: any) => {
      acc[event.type] = createJsonPayloadExampleFromSchema(event.payload);
      return acc;
    },
    {} as Record<string, string>,
  );

  const { getLimit, loading: limitsLoading } = useOrganizationLimits([
    "APPS",
    "ATTACHED_APPS",
    "WEBHOOKS",
  ]);
  const appsLimit = getLimit("APPS");
  const attachedAppsLimit = getLimit("ATTACHED_APPS");
  const webhooksLimit = getLimit("WEBHOOKS");

  const activeLimits = [appsLimit, attachedAppsLimit, webhooksLimit].filter(
    Boolean,
  );

  return (
    <div className="wp-fade-in">
      <PageHeader
        title="Developer Documentation"
        subtitle="Complete reference for building apps, extensions, and integrations on the WellPlayed platform"
        showBreadcrumbs={false}
      />

      {!limitsLoading && activeLimits.length > 0 && (
        <SimpleGrid cols={{ base: 1, md: 3 }} mb="lg">
          {appsLimit && <LimitUsageBar limit={appsLimit} label="Apps" />}
          {attachedAppsLimit && (
            <LimitUsageBar limit={attachedAppsLimit} label="Attached Apps" />
          )}
          {webhooksLimit && (
            <LimitUsageBar limit={webhooksLimit} label="Webhooks" />
          )}
        </SimpleGrid>
      )}

      {/* Table of Contents */}
      <Card className={classes.card} p="md" radius="md" mb="xl">
        <Text fw={600} mb="sm">
          Table of Contents
        </Text>
        <SimpleGrid cols={{ base: 1, md: 3 }}>
          <List spacing={4} size="sm">
            <List.Item>
              <Anchor href="#getting-started">Getting Started</Anchor>
            </List.Item>
            <List.Item>
              <Anchor href="#ai-development">AI-Assisted Development</Anchor>
            </List.Item>
            <List.Item>
              <Anchor href="#cli-reference">CLI Reference</Anchor>
            </List.Item>
            <List.Item>
              <Anchor href="#app-manifest">App Manifest</Anchor>
            </List.Item>
            <List.Item>
              <Anchor href="#extensions">Extensions</Anchor>
            </List.Item>
          </List>
          <List spacing={4} size="sm">
            <List.Item>
              <Anchor href="#frontend-modules">Frontend Modules</Anchor>
            </List.Item>
            <List.Item>
              <Anchor href="#module-ui">Module UI Components</Anchor>
            </List.Item>
            <List.Item>
              <Anchor href="#web-components">Web Components</Anchor>
            </List.Item>
            <List.Item>
              <Anchor href="#sdk-packages">SDK Packages Reference</Anchor>
            </List.Item>
            <List.Item>
              <Anchor href="#building-with-react">Building with React</Anchor>
            </List.Item>
            <List.Item>
              <Anchor href="#release-workflow">Release Workflow</Anchor>
            </List.Item>
            <List.Item>
              <Anchor href="#overview">API Overview</Anchor>
            </List.Item>
            <List.Item>
              <Anchor href="#authentication">Authentication</Anchor>
            </List.Item>
            <List.Item>
              <Anchor href="#token-types">Token Types</Anchor>
            </List.Item>
            <List.Item>
              <Anchor href="#oauth-endpoints">OAuth Endpoints</Anchor>
            </List.Item>
            <List.Item>
              <Anchor href="#login-flows">Login Flows</Anchor>
            </List.Item>
          </List>
          <List spacing={4} size="sm">
            <List.Item>
              <Anchor href="#identity-linking">Identity Linking</Anchor>
            </List.Item>
            <List.Item>
              <Anchor href="#consent-and-logout">Consent &amp; Logout</Anchor>
            </List.Item>
            <List.Item>
              <Anchor href="#apps">Organization Apps</Anchor>
            </List.Item>
            <List.Item>
              <Anchor href="#webhooks">Webhooks</Anchor>
            </List.Item>
            <List.Item>
              <Anchor href="#webhook-events">Webhook Events</Anchor>
            </List.Item>
            <List.Item>
              <Anchor href="#journal">Journal</Anchor>
            </List.Item>
            <List.Item>
              <Anchor href="#graphql">GraphQL API Reference</Anchor>
            </List.Item>
            <List.Item>
              <Anchor href="#data-model">Data Model</Anchor>
            </List.Item>
          </List>
        </SimpleGrid>
      </Card>

      <Stack gap="xl">
        {/* ===================== AI-ASSISTED DEVELOPMENT ===================== */}
        <div className={classes.section}>
          <SectionHeader
            icon={<IconSparkles size={18} />}
            title={
              <>
                <span>AI-Assisted Development</span>
                <Badge color="cyan" variant="light" ml="xs">
                  Beta
                </Badge>
              </>
            }
            id="ai-development"
          />
          <Text mb="md">
            Build faster on WellPlayed with Claude Code, Cursor, ChatGPT,
            Perplexity, and any other MCP-capable AI tool. We publish canonical
            docs, a live MCP server, and SDK-bundled skills so your AI can
            scaffold complete projects correctly.
          </Text>

          <Title order={4} mb="xs">
            What you get
          </Title>
          <SimpleGrid cols={{ base: 1, md: 3 }} mb="lg">
            <Card className={classes.card} p="md" radius="md">
              <Text fw={600} mb="xs">
                Remote MCP server
              </Text>
              <Text size="sm" c="dimmed">
                Live, read-only tools for schema search, type lookup, operation
                docs, deprecation checks, and more. No install — just point your
                AI at the URL.
              </Text>
            </Card>
            <Card className={classes.card} p="md" radius="md">
              <Text fw={600} mb="xs">
                <Code>llms.txt</Code>
              </Text>
              <Text size="sm" c="dimmed">
                Machine-readable index of our docs, schema, and SDKs. Works with
                any AI tool (ChatGPT, Perplexity, Gemini) that supports file
                URLs.
              </Text>
            </Card>
            <Card className={classes.card} p="md" radius="md">
              <Text fw={600} mb="xs">
                SDK-bundled skills
              </Text>
              <Text size="sm" c="dimmed">
                Install <Code>@well-played.gg/react-sdk</Code> or{" "}
                <Code>/typescript-sdk</Code> and Claude Code picks up a skill
                pinned to your SDK version — so generated code always compiles.
              </Text>
            </Card>
          </SimpleGrid>

          <Title order={4} mb="xs">
            Zero-click setup
            <Badge color="green" variant="light" ml="xs">
              Recommended
            </Badge>
          </Title>
          <Text mb="sm">
            Three commands, no console clicks. The{" "}
            <Code>@well-played.gg/cli</Code> <Code>create-app</Code> command
            authenticates you, registers an OAuth app in the organization of
            your choice, scaffolds a Vite + React + TypeScript project,
            installs the SDK, copies the Claude Code skills into{" "}
            <Code>.claude/skills/</Code>, writes <Code>.mcp.json</Code>, and
            fills in <Code>.env.local</Code>. The project is Claude-Code-ready
            the moment it finishes.
          </Text>
          <Card className={classes.card} p="md" radius="md" mb="sm">
            <GqlBlock>{ZERO_CLICK_SNIPPET}</GqlBlock>
          </Card>
          <Text mb="sm" size="sm">
            <Code>wellplayed login</Code> opens your browser to authenticate
            via WellPlayed&apos;s OAuth provider; the token issued is a
            standard OAuth2 access token your CLI uses for every subsequent
            call.
          </Text>
          <Text mb="md" size="sm">
            Then ask Claude Code to build something (see Example prompts
            below). The MCP tools, skill content, and OAuth credentials are
            all already in place.
          </Text>

          <Title order={4} mb="xs">
            Quickstart for Claude Code (existing project)
          </Title>
          <Text mb="sm" size="sm">
            If you already have a project and want to add WellPlayed manually,
            follow these steps. For a fresh project, use the zero-click flow
            above instead.
          </Text>
          <Text mb="sm" size="sm">
            1. Install the SDK in your project:
          </Text>
          <Card className={classes.card} p="md" radius="md" mb="sm">
            <GqlBlock>
              {`yarn add @well-played.gg/react-sdk
# or for non-React stacks:
yarn add @well-played.gg/typescript-sdk`}
            </GqlBlock>
          </Card>
          <Text mb="sm" size="sm">
            2. Add the MCP server to your <Code>.mcp.json</Code>:
          </Text>
          <Card className={classes.card} p="md" radius="md" mb="sm">
            <GqlBlock>{CLAUDE_CODE_INSTALL_SNIPPET}</GqlBlock>
          </Card>
          <Text mb="sm" size="sm">
            3. Install the bundled skills (required — Claude Code doesn't
            auto-discover skills in <Code>node_modules</Code>):
          </Text>
          <Card className={classes.card} p="md" radius="md" mb="sm">
            <GqlBlock>{`npx @well-played.gg/cli install-skills`}</GqlBlock>
          </Card>
          <Text mb="md" size="sm">
            4. Ask Claude Code to build something:
          </Text>
          <Card className={classes.card} p="md" radius="md" mb="lg">
            <GqlBlock>{`"Scaffold a Next.js app against my WellPlayed organization with tournaments and events."`}</GqlBlock>
          </Card>

          <Title order={4} mb="xs">
            Quickstart for Cursor, Windsurf, other MCP-capable editors
          </Title>
          <Text mb="md" size="sm">
            Add the same MCP URL to your editor's MCP config. See your editor's
            docs for the exact file location.
          </Text>

          <Title order={4} mb="xs">
            Quickstart for ChatGPT, Perplexity, Gemini
          </Title>
          <Text mb="md" size="sm">
            Attach{" "}
            <Anchor href={LLMS_TXT_URL} target="_blank" rel="noreferrer">
              {LLMS_TXT_URL}
            </Anchor>{" "}
            as a file or reference URL, or paste{" "}
            <Anchor href={LLMS_FULL_TXT_URL} target="_blank" rel="noreferrer">
              llms-full.txt
            </Anchor>{" "}
            for a single-shot full reference.
          </Text>

          <Title order={4} mb="xs">
            MCP tools (read-only in v1)
          </Title>
          <Table striped className={classes.statusTable} mb="lg">
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Tool</Table.Th>
                <Table.Th>Purpose</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              <Table.Tr>
                <Table.Td>
                  <Code>search_schema</Code>
                </Table.Td>
                <Table.Td>
                  Ranked search over schema types and operations
                </Table.Td>
              </Table.Tr>
              <Table.Tr>
                <Table.Td>
                  <Code>get_type</Code>
                </Table.Td>
                <Table.Td>
                  Full type doc — fields, deprecations, relations
                </Table.Td>
              </Table.Tr>
              <Table.Tr>
                <Table.Td>
                  <Code>get_operation</Code>
                </Table.Td>
                <Table.Td>
                  Operation doc — args, return, permissions, error codes
                </Table.Td>
              </Table.Tr>
              <Table.Tr>
                <Table.Td>
                  <Code>list_operations</Code>
                </Table.Td>
                <Table.Td>
                  Filter operations by entity, kind, or permission
                </Table.Td>
              </Table.Tr>
              <Table.Tr>
                <Table.Td>
                  <Code>get_enum</Code>
                </Table.Td>
                <Table.Td>
                  Enum values with descriptions and deprecations
                </Table.Td>
              </Table.Tr>
              <Table.Tr>
                <Table.Td>
                  <Code>get_guide</Code>
                </Table.Td>
                <Table.Td>
                  Full guide markdown (auth, webhooks, pagination, etc.)
                </Table.Td>
              </Table.Tr>
              <Table.Tr>
                <Table.Td>
                  <Code>list_guides</Code>
                </Table.Td>
                <Table.Td>Catalog of available guides</Table.Td>
              </Table.Tr>
              <Table.Tr>
                <Table.Td>
                  <Code>list_deprecations</Code>
                </Table.Td>
                <Table.Td>Structured deprecation entries</Table.Td>
              </Table.Tr>
              <Table.Tr>
                <Table.Td>
                  <Code>get_pack_info</Code>
                </Table.Td>
                <Table.Td>
                  Pack version, gitsha, schema hash, build time
                </Table.Td>
              </Table.Tr>
            </Table.Tbody>
          </Table>

          <Title order={4} mb="xs">
            Canonical URLs
          </Title>
          <Table striped className={classes.statusTable} mb="lg">
            <Table.Tbody>
              <Table.Tr>
                <Table.Td>MCP endpoint</Table.Td>
                <Table.Td>
                  <Anchor href={MCP_URL} target="_blank" rel="noreferrer">
                    {MCP_URL}
                  </Anchor>
                </Table.Td>
              </Table.Tr>
              <Table.Tr>
                <Table.Td>llms.txt</Table.Td>
                <Table.Td>
                  <Anchor href={LLMS_TXT_URL} target="_blank" rel="noreferrer">
                    {LLMS_TXT_URL}
                  </Anchor>
                </Table.Td>
              </Table.Tr>
              <Table.Tr>
                <Table.Td>llms-full.txt</Table.Td>
                <Table.Td>
                  <Anchor
                    href={LLMS_FULL_TXT_URL}
                    target="_blank"
                    rel="noreferrer"
                  >
                    {LLMS_FULL_TXT_URL}
                  </Anchor>
                </Table.Td>
              </Table.Tr>
              <Table.Tr>
                <Table.Td>Schema SDL</Table.Td>
                <Table.Td>
                  <Anchor
                    href={SCHEMA_SDL_URL}
                    target="_blank"
                    rel="noreferrer"
                  >
                    {SCHEMA_SDL_URL}
                  </Anchor>
                </Table.Td>
              </Table.Tr>
            </Table.Tbody>
          </Table>

          <Title order={4} mb="xs">
            Example prompts
          </Title>
          <List spacing="xs" mb="lg">
            {EXAMPLE_PROMPTS.map((p) => (
              <List.Item key={p.title}>
                <Text fw={600} span>
                  {p.title}:
                </Text>{" "}
                <Text size="sm" span c="dimmed">
                  {p.body}
                </Text>
              </List.Item>
            ))}
          </List>

          <Title order={4} mb="xs">
            Security &amp; data
          </Title>
          <Alert icon={<IconInfoCircle />} color="indigo" variant="light" mb="md">
            The MCP server is <strong>read-only</strong> and{" "}
            <strong>anonymous</strong> — it serves public schema, docs, and
            generated templates. It does not have access to your organization's
            data. Generated code uses the standard OIDC auth flow with your own
            app credentials; there is no custom auth surface.
          </Alert>
        </div>

        {/* ===================== GETTING STARTED ===================== */}
        <div className={classes.section}>
          <SectionHeader
            icon={<IconRocket size={18} />}
            title="Getting Started"
            id="getting-started"
          />
          <Text mb="md">
            Build marketplace apps that extend the WellPlayed platform with
            custom extensions and frontend modules. This guide walks you through
            the full development workflow from project scaffolding to
            deployment.
          </Text>

          <Title order={4} mb="xs">
            Prerequisites
          </Title>
          <List size="sm" spacing="xs" mb="md">
            <List.Item>
              <strong>Node.js</strong> v18 or later
            </List.Item>
            <List.Item>
              <strong>npm</strong> or <strong>yarn</strong> package manager
            </List.Item>
            <List.Item>
              A WellPlayed developer account with an organization
            </List.Item>
          </List>

          <Title order={4} mb="xs">
            Install the CLI
          </Title>
          <Card className={classes.card} p="md" radius="md" mb="md">
            <GqlBlock>{`npm install -g @well-played.gg/cli`}</GqlBlock>
          </Card>

          <Title order={4} mb="xs">
            Quick Start
          </Title>
          <Text mb="sm" size="sm">
            The typical development workflow follows four steps:
          </Text>
          <Card className={classes.card} p="md" radius="md" mb="md">
            <GqlBlock>
              {`# 1. Scaffold a new app project
wellplayed init my-app

# 2. Authenticate with your developer account
wellplayed login

# 3. Start the local development server
cd my-app
wellplayed dev --port 3000

# 4. Deploy to the marketplace
wellplayed deploy --app-id <your-app-id>`}
            </GqlBlock>
          </Card>

          <Card className={classes.card} p="md" radius="md" mb="md">
            <Text fw={600} mb="xs" size="sm">
              Development Flow
            </Text>
            <GqlBlock>
              {`  init          login         dev           deploy
┌─────────┐  ┌─────────┐  ┌─────────┐  ┌─────────┐
│ Scaffold │─→│  Auth   │─→│  Local  │─→│  Build  │
│ project  │  │  device │  │  server │  │& upload │
└─────────┘  └─────────┘  └─────────┘  └─────────┘
     │                          │              │
     v                          v              v
 wellplayed.json        http://localhost   Release created
 + manifest.json        + hot reload       + review process`}
            </GqlBlock>
          </Card>

          <Alert icon={<IconInfoCircle />} color="indigo" variant="light">
            After running <Code>wellplayed init</Code>, your project will
            contain a <Code>wellplayed.json</Code> config file and a{" "}
            <Code>manifest.json</Code> that declares your app&apos;s
            capabilities. Edit the manifest to configure extensions and modules.
          </Alert>
        </div>

        {/* ===================== CLI REFERENCE ===================== */}
        <div className={classes.section}>
          <SectionHeader
            icon={<IconTerminal2 size={18} />}
            title="CLI Reference"
            id="cli-reference"
          />
          <Text mb="md">
            The <Code>@well-played.gg/cli</Code> provides commands for
            scaffolding, developing, and deploying marketplace apps.
          </Text>

          <Table
            striped
            highlightOnHover
            className={classes.statusTable}
            mb="md"
          >
            <thead>
              <tr>
                <th>Command</th>
                <th>Description</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>
                  <Code>wellplayed init [name]</Code>
                </td>
                <td>
                  Scaffold a new app project with boilerplate files, manifest,
                  and config
                </td>
              </tr>
              <tr>
                <td>
                  <Code>wellplayed login</Code>
                </td>
                <td>
                  Authenticate using the device authorization flow (opens
                  browser)
                </td>
              </tr>
              <tr>
                <td>
                  <Code>wellplayed dev --port &lt;port&gt;</Code>
                </td>
                <td>
                  Start a local development server with hot reload for
                  extensions and modules
                </td>
              </tr>
              <tr>
                <td>
                  <Code>wellplayed deploy --app-id &lt;id&gt;</Code>
                </td>
                <td>
                  Build the project, bundle assets, and upload as a new release
                </td>
              </tr>
              <tr>
                <td>
                  <Code>wellplayed graphql [query]</Code>
                </td>
                <td>
                  Run a GraphQL query or mutation against the WellPlayed API
                  (modeled on <Code>gh api graphql</Code>). Reuses your{" "}
                  <Code>wellplayed login</Code> credentials.
                </td>
              </tr>
            </tbody>
          </Table>

          <Title order={4} mb="xs">
            GraphQL Queries from the CLI
          </Title>
          <Text mb="sm" size="sm">
            Use <Code>wellplayed graphql</Code> for ad-hoc queries, scripts, and
            CI automation. The query can be passed inline, loaded from a file
            with <Code>-f</Code>, or piped on stdin. Variables come from{" "}
            <Code>-F key=value</Code> (JSON-coerced),{" "}
            <Code>--raw-field</Code> (string-only), or a JSON file via{" "}
            <Code>--variables</Code>. Set the organization context with{" "}
            <Code>-o, --org &lt;shortId&gt;</Code>.
          </Text>
          <Card className={classes.card} p="md" radius="md" mb="md">
            <GqlBlock>
              {`# Inline query
wellplayed graphql 'query { currentAccount { id email } }'

# Mutation with typed nested variables, scoped to an org
wellplayed graphql -f ./create-team.graphql --org my-org \\
  -F input.name=Phoenix -F input.players=10

# Load variables from a JSON file
wellplayed graphql -f ./op.graphql --variables ./vars.json --org my-org

# Pipe \`data\` only into jq for scripting
wellplayed graphql 'query { currentAccount { id } }' --data-only | jq '.currentAccount.id'`}
            </GqlBlock>
          </Card>
          <Text mb="md" size="sm" c="dimmed">
            By default <Code>wellplayed graphql</Code> prints the full GraphQL
            envelope (<Code>{"{ data, errors, extensions }"}</Code>) and exits
            with code <Code>1</Code> if <Code>errors[]</Code> is non-empty. Pass{" "}
            <Code>--data-only</Code> for clean pipeable output that throws on
            any GraphQL error.
          </Text>

          <Title order={4} mb="xs">
            Configuration File
          </Title>
          <Text mb="sm" size="sm">
            The <Code>wellplayed.json</Code> file in your project root
            configures the CLI behavior:
          </Text>
          <Card className={classes.card} p="md" radius="md">
            <GqlBlock>
              {`{
  "appId": "your-app-id",
  "buildCommand": "npm run build",
  "outputDir": "dist",
  "devPort": 3000,
  "manifestPath": "manifest.json"
}`}
            </GqlBlock>
          </Card>
        </div>

        {/* ===================== APP MANIFEST ===================== */}
        <div className={classes.section}>
          <SectionHeader
            icon={<IconPackage size={18} />}
            title="App Manifest"
            id="app-manifest"
          />
          <Text mb="md">
            The <Code>manifest.json</Code> file declares everything your app
            provides: permissions, extensions, webhook subscriptions, metafield
            namespaces, and frontend modules. The platform reads this manifest
            during deployment to register your app&apos;s capabilities.
          </Text>

          <Card className={classes.card} p="md" radius="md" mb="md">
            <GqlBlock>
              {`{
  "version": "1.0",
  "permissions": [
    "organization:tournament:view",
    "organization:tournament:update",
    "organization:players:view",
    "organization:event:view"
  ],
  "extensions": [
    {
      "type": "tab",
      "label": "Analytics",
      "activePages": ["/tournaments/:tournamentId"],
      "size": { "maxWidth": 1200, "maxHeight": 600 }
    }
  ],
  "webhookUrl": "https://your-app.com/webhooks",
  "webhookEvents": [
    "TOURNAMENT_TEAM_STATUS_UPDATED",
    "TOURNAMENT_TEAM_DELETED"
  ],
  "metafieldNamespaces": [
    "my-app-data"
  ],
  "frontendModules": [
    {
      "name": "leaderboard-widget",
      "displayName": "Leaderboard Widget",
      "description": "Displays a tournament leaderboard",
      "exportName": "default",
      "version": "1.0.0",
      "propsSchema": [
        { "key": "title", "type": "string", "label": "Title", "defaultValue": "Leaderboard" },
        { "key": "limit", "type": "number", "label": "Max entries", "defaultValue": "10" },
        { "key": "tournamentId", "type": "route-param", "label": "Tournament", "param": "tournamentId" }
      ],
      "events": [
        { "name": "player-selected", "description": "Emitted when a player row is clicked" }
      ]
    }
  ]
}`}
            </GqlBlock>
          </Card>

          <Table
            striped
            highlightOnHover
            className={classes.statusTable}
            mb="md"
          >
            <thead>
              <tr>
                <th>Field</th>
                <th>Type</th>
                <th>Description</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>
                  <Code>version</Code>
                </td>
                <td>String</td>
                <td>Manifest schema version (currently &quot;1.0&quot;)</td>
              </tr>
              <tr>
                <td>
                  <Code>permissions</Code>
                </td>
                <td>String[]</td>
                <td>
                  OrganizationPermission IDs the app requires (e.g.{" "}
                  <Code>organization:tournament:view</Code>)
                </td>
              </tr>
              <tr>
                <td>
                  <Code>extensions</Code>
                </td>
                <td>Object[]</td>
                <td>
                  UI extensions with <Code>activePages</Code>
                  and optional <Code>size</Code> (maxWidth/maxHeight in px).
                  Bundle CDN path is provided by the API after deploy.
                </td>
              </tr>
              <tr>
                <td>
                  <Code>webhookUrl</Code>
                </td>
                <td>String?</td>
                <td>
                  URL to receive webhook event POST requests when app is
                  installed
                </td>
              </tr>
              <tr>
                <td>
                  <Code>webhookEvents</Code>
                </td>
                <td>String[]</td>
                <td>Webhook event types the app subscribes to</td>
              </tr>
              <tr>
                <td>
                  <Code>metafieldNamespaces</Code>
                </td>
                <td>String[]</td>
                <td>
                  Custom data namespaces the app can read/write on entities
                </td>
              </tr>
              <tr>
                <td>
                  <Code>frontendModules</Code>
                </td>
                <td>Object[]</td>
                <td>
                  Embeddable UI modules for platform pages. Each requires{" "}
                  <Code>name</Code>, <Code>displayName</Code>,{" "}
                  <Code>version</Code>, and optional <Code>propsSchema</Code>{" "}
                  and <Code>events</Code>. Bundle CDN path is provided by the
                  API after deploy.
                </td>
              </tr>
            </tbody>
          </Table>
        </div>

        {/* ===================== EXTENSION POINTS ===================== */}
        <div className={classes.section}>
          <SectionHeader
            icon={<IconPuzzle size={18} />}
            title="Extensions"
            id="extensions"
          />
          <Text mb="md">
            Extensions allow your app to inject custom UI into specific pages
            within the platform. Each extension renders inside a sandboxed
            iframe and communicates with the host page via the module SDK.
            Extensions use <Code>activePages</Code> to declare which URL
            patterns they should appear on, and can access route params from
            those URLs.
          </Text>

          <Title order={4} mb="xs">
            Available Extension Types
          </Title>
          <Table
            striped
            highlightOnHover
            className={classes.statusTable}
            mb="md"
          >
            <thead>
              <tr>
                <th>Type</th>
                <th>Description</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>
                  <Code>tab</Code>
                </td>
                <td>
                  Adds a custom tab to any detail page (use activePages to
                  target specific pages)
                </td>
              </tr>
              <tr>
                <td>
                  <Code>dashboard.widget</Code>
                </td>
                <td>Renders a widget card on the admin dashboard</td>
              </tr>
              <tr>
                <td>
                  <Code>admin.navigation</Code>
                </td>
                <td>Adds a navigation entry to the admin sidebar</td>
              </tr>
            </tbody>
          </Table>

          <Title order={4} mb="xs">
            Extension Configuration
          </Title>
          <Text mb="sm" size="sm">
            Each extension in the manifest specifies a type, label, activePages
            (URL patterns), and optional size constraints (maxWidth/maxHeight in
            pixels). The bundle CDN path is provided by the API after deploy:
          </Text>
          <Card className={classes.card} p="md" radius="md" mb="md">
            <GqlBlock>
              {`{
  "type": "tab",
  "label": "My Custom Tab",
  "activePages": [
    "/tournaments/:tournamentId",
    "/events/:eventId"
  ],
  "size": {
    "maxWidth": 1200,
    "maxHeight": 400
  }
}`}
            </GqlBlock>
          </Card>

          <Title order={4} mb="xs">
            Extension Source Example
          </Title>
          <Card className={classes.card} p="md" radius="md">
            <GqlBlock>
              {`import { defineExtension } from "@well-played.gg/module-sdk";

export default defineExtension({
  mount(container, context) {
    // context.entityId — the tournament/player/event ID
    // context.organizationId — the current organization
    // context.token — auth token for API calls

    container.innerHTML = \`
      <div style="padding: 16px;">
        <h3>Custom Analytics</h3>
        <p>Tournament: \${context.entityId}</p>
      </div>
    \`;
  },

  unmount(container) {
    container.innerHTML = "";
  },
});`}
            </GqlBlock>
          </Card>

          <Title order={4} mt="md" mb="xs">
            React Extension Example
          </Title>
          <Text mb="sm" size="sm">
            You can use React to build extensions by mounting a React root in
            the <Code>mount</Code> lifecycle hook. Install <Code>react</Code>,{" "}
            <Code>react-dom</Code>, and <Code>@well-played.gg/module-sdk</Code>{" "}
            in your project:
          </Text>
          <Card className={classes.card} p="md" radius="md" mb="md">
            <GqlBlock>{`npm install react react-dom @well-played.gg/module-sdk`}</GqlBlock>
          </Card>
          <Card className={classes.card} p="md" radius="md" mb="md">
            <GqlBlock>
              {`// extensions/tournament-analytics.tsx
import { defineExtension } from "@well-played.gg/module-sdk";
import { createRoot, Root } from "react-dom/client";
import { TournamentAnalytics } from "./TournamentAnalytics";

let root: Root | null = null;

export default defineExtension({
  mount(container, context) {
    root = createRoot(container);
    root.render(
      <TournamentAnalytics
        entityId={context.entityId}
        organizationId={context.organizationId}
        token={context.token}
      />
    );
  },

  unmount(container) {
    root?.unmount();
    root = null;
  },
});`}
            </GqlBlock>
          </Card>
          <Card className={classes.card} p="md" radius="md">
            <GqlBlock>
              {`// extensions/TournamentAnalytics.tsx
import { useState, useEffect } from "react";

interface Props {
  entityId: string;
  organizationId: string;
  token: string;
}

export function TournamentAnalytics({ entityId, organizationId, token }: Props) {
  const [stats, setStats] = useState<{ teams: number; matches: number } | null>(null);

  useEffect(() => {
    fetch("https://api.warrior.well-played.gg/graphql", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "x-module-token": token,
      },
      body: JSON.stringify({
        query: \`query ($id: String!) {
          tournament(id: $id) { id name teamsCount matchesCount }
        }\`,
        variables: { id: entityId },
      }),
    })
      .then((res) => res.json())
      .then((data) => {
        const t = data.data.tournament;
        setStats({ teams: t.teamsCount, matches: t.matchesCount });
      });
  }, [entityId, organizationId, token]);

  if (!stats) return <div style={{ padding: 16 }}>Loading...</div>;

  return (
    <div style={{ padding: 16 }}>
      <h3>Tournament Analytics</h3>
      <p>Teams: {stats.teams}</p>
      <p>Matches: {stats.matches}</p>
    </div>
  );
}`}
            </GqlBlock>
          </Card>

          <Alert
            icon={<IconInfoCircle />}
            color="indigo"
            variant="light"
            mt="md"
          >
            Extensions run in a sandboxed iframe. Use <Code>context.token</Code>{" "}
            to make authenticated API calls back to the WellPlayed GraphQL API.
            Pass it as the <Code>x-module-token</Code> header — no{" "}
            <Code>organization-id</Code> or <Code>Authorization</Code> header is
            needed. The token is scoped to the permissions granted during app
            installation.
          </Alert>
        </div>

        {/* ===================== FRONTEND MODULES ===================== */}
        <div className={classes.section}>
          <SectionHeader
            icon={<IconCode size={18} />}
            title="Frontend Modules"
            id="frontend-modules"
          />
          <Text mb="md">
            Frontend modules are embeddable UI components that platform
            administrators can place on generated pages using Liquid templates.
            Modules are built with the <Code>@well-played.gg/module-sdk</Code>{" "}
            and support configurable props, events, and GraphQL queries.
          </Text>

          <Title order={4} mb="xs">
            Module SDK
          </Title>
          <Card className={classes.card} p="md" radius="md" mb="md">
            <GqlBlock>{`npm install @well-played.gg/module-sdk`}</GqlBlock>
          </Card>

          <Title order={4} mb="xs">
            defineModule API
          </Title>
          <Text mb="sm" size="sm">
            Use <Code>defineModule()</Code> to declare a module with its props
            schema, lifecycle hooks, and event handlers:
          </Text>
          <Card className={classes.card} p="md" radius="md" mb="md">
            <GqlBlock>
              {`import { defineModule } from "@well-played.gg/module-sdk";

export default defineModule({
  // Props schema — configurable by the page editor
  propsSchema: {
    title: { type: "string", default: "My Widget" },
    limit: { type: "number", default: 5 },
    showHeader: { type: "boolean", default: true },
    theme: {
      type: "select",
      options: ["light", "dark", "auto"],
      default: "auto",
    },
    tournamentId: { type: "route-param", param: "tournamentId" },
  },

  // Called when the module mounts in the page
  mount(container, context) {
    const { props, organizationId, token } = context;
    container.innerHTML = \`<h3>\${props.title}</h3>\`;
  },

  // Called when props change (e.g. page editor updates)
  update(container, context) {
    container.querySelector("h3").textContent = context.props.title;
  },

  // Called when the module is removed from the page
  unmount(container) {
    container.innerHTML = "";
  },
});`}
            </GqlBlock>
          </Card>

          <Title order={4} mb="xs">
            Props Schema Types
          </Title>
          <Table
            striped
            highlightOnHover
            className={classes.statusTable}
            mb="md"
          >
            <thead>
              <tr>
                <th>Type</th>
                <th>Description</th>
                <th>Example</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>
                  <Code>string</Code>
                </td>
                <td>Text input</td>
                <td>
                  <Code>{`{ type: "string", default: "Hello" }`}</Code>
                </td>
              </tr>
              <tr>
                <td>
                  <Code>number</Code>
                </td>
                <td>Numeric input</td>
                <td>
                  <Code>{`{ type: "number", default: 10 }`}</Code>
                </td>
              </tr>
              <tr>
                <td>
                  <Code>boolean</Code>
                </td>
                <td>Toggle switch</td>
                <td>
                  <Code>{`{ type: "boolean", default: true }`}</Code>
                </td>
              </tr>
              <tr>
                <td>
                  <Code>select</Code>
                </td>
                <td>Dropdown with predefined options</td>
                <td>
                  <Code>{`{ type: "select", options: ["a", "b"] }`}</Code>
                </td>
              </tr>
              <tr>
                <td>
                  <Code>route-param</Code>
                </td>
                <td>
                  Automatically populated from the current page URL parameter
                </td>
                <td>
                  <Code>{`{ type: "route-param", param: "eventId" }`}</Code>
                </td>
              </tr>
            </tbody>
          </Table>

          <Title order={4} mb="xs">
            Events System
          </Title>
          <Text mb="sm" size="sm">
            Modules can communicate with each other and with the host page
            through the events system:
          </Text>
          <Card className={classes.card} p="md" radius="md" mb="md">
            <GqlBlock>
              {`// Emit an event from your module
context.emit("player-selected", { playerId: "p_123" });

// Listen for events from other modules or the host page
context.on("filter-changed", (payload) => {
  // Re-render with new filter
  refreshData(payload.filter);
});`}
            </GqlBlock>
          </Card>

          <Title order={4} mb="xs">
            React Hooks
          </Title>
          <Text mb="sm" size="sm">
            If you prefer React, the module SDK provides hooks for common
            operations:
          </Text>
          <Table
            striped
            highlightOnHover
            className={classes.statusTable}
            mb="md"
          >
            <thead>
              <tr>
                <th>Hook</th>
                <th>Description</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>
                  <Code>useModuleContext()</Code>
                </td>
                <td>
                  Access the module context (props, organizationId, token)
                </td>
              </tr>
              <tr>
                <td>
                  <Code>useModuleQuery(query, variables)</Code>
                </td>
                <td>Execute a GraphQL query using the module token</td>
              </tr>
              <tr>
                <td>
                  <Code>useModuleMutation(mutation)</Code>
                </td>
                <td>Execute a GraphQL mutation using the module token</td>
              </tr>
              <tr>
                <td>
                  <Code>useModuleEvent(eventName, handler)</Code>
                </td>
                <td>Listen for events from other modules</td>
              </tr>
              <tr>
                <td>
                  <Code>useModuleEmit()</Code>
                </td>
                <td>
                  Returns an emit function to send events to other modules
                </td>
              </tr>
            </tbody>
          </Table>

          <Title order={4} mb="xs">
            Building a React Module
          </Title>
          <Text mb="sm" size="sm">
            To build a module with React, use <Code>defineReactModule()</Code>{" "}
            which handles mounting and unmounting React for you. Your component
            receives all module context as props:
          </Text>
          <Card className={classes.card} p="md" radius="md" mb="md">
            <GqlBlock>
              {`// modules/leaderboard-widget.tsx
import { defineReactModule } from "@well-played.gg/module-sdk/react";
import {
  useModuleContext,
  useModuleQuery,
  useModuleEvent,
  useModuleEmit,
} from "@well-played.gg/module-sdk/react";

interface LeaderboardProps {
  title: string;
  limit: number;
  tournamentId: string;
}

function LeaderboardWidget() {
  const { props } = useModuleContext<LeaderboardProps>();
  const emit = useModuleEmit();

  // Fetch leaderboard data using the module token
  const { data, loading, error } = useModuleQuery<{
    leaderboard: { rank: number; name: string; score: number }[];
  }>(
    \`query ($tournamentId: String!, $limit: Int!) {
      leaderboard(tournamentId: $tournamentId, limit: $limit) {
        rank name score
      }
    }\`,
    { tournamentId: props.tournamentId, limit: props.limit }
  );

  // Listen for filter events from other modules on the page
  useModuleEvent("filter-changed", (payload) => {
    // React to filter changes from sibling modules
    console.log("Filter updated:", payload);
  });

  if (loading) return <div>Loading leaderboard...</div>;
  if (error) return <div>Failed to load leaderboard</div>;

  return (
    <div style={{ fontFamily: "sans-serif" }}>
      <h3>{props.title}</h3>
      <table style={{ width: "100%", borderCollapse: "collapse" }}>
        <thead>
          <tr>
            <th style={{ textAlign: "left", padding: 8 }}>Rank</th>
            <th style={{ textAlign: "left", padding: 8 }}>Player</th>
            <th style={{ textAlign: "right", padding: 8 }}>Score</th>
          </tr>
        </thead>
        <tbody>
          {data?.leaderboard.map((entry) => (
            <tr
              key={entry.rank}
              style={{ borderTop: "1px solid #eee", cursor: "pointer" }}
              onClick={() => emit("player-selected", { name: entry.name })}
            >
              <td style={{ padding: 8 }}>{entry.rank}</td>
              <td style={{ padding: 8 }}>{entry.name}</td>
              <td style={{ padding: 8, textAlign: "right" }}>{entry.score}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// defineReactModule handles createRoot / unmount lifecycle automatically
export default defineReactModule({
  propsSchema: {
    title: { type: "string", default: "Leaderboard" },
    limit: { type: "number", default: 10 },
    tournamentId: { type: "route-param", param: "tournamentId" },
  },
  component: LeaderboardWidget,
});`}
            </GqlBlock>
          </Card>

          <Title order={4} mb="xs">
            React Extension with Module SDK Hooks
          </Title>
          <Text mb="sm" size="sm">
            Extensions can also use React with the same hooks. Wrap your
            extension component with <Code>defineReactExtension()</Code>:
          </Text>
          <Card className={classes.card} p="md" radius="md" mb="md">
            <GqlBlock>
              {`// extensions/team-roster.tsx
import { defineReactExtension } from "@well-played.gg/module-sdk/react";
import {
  useModuleContext,
  useModuleQuery,
  useModuleMutation,
} from "@well-played.gg/module-sdk/react";
import { useState } from "react";

function TeamRoster() {
  const { entityId } = useModuleContext();
  const [note, setNote] = useState("");

  const { data, loading, refetch } = useModuleQuery<{
    tournament: {
      teams: { id: string; name: string; status: string }[];
    };
  }>(
    \`query ($id: String!) {
      tournament(id: $id) {
        teams { id name status }
      }
    }\`,
    { id: entityId }
  );

  const [updateNote] = useModuleMutation(\`
    mutation ($teamId: String!, $input: UpdateMetafieldInput!) {
      updateMetafield(entityId: $teamId, input: $input) { value }
    }
  \`);

  if (loading) return <div style={{ padding: 16 }}>Loading roster...</div>;

  return (
    <div style={{ padding: 16, fontFamily: "sans-serif" }}>
      <h3>Team Roster</h3>
      <ul style={{ listStyle: "none", padding: 0 }}>
        {data?.tournament.teams.map((team) => (
          <li
            key={team.id}
            style={{
              display: "flex",
              justifyContent: "space-between",
              padding: "8px 0",
              borderBottom: "1px solid #eee",
            }}
          >
            <span>{team.name}</span>
            <span style={{
              color: team.status === "CONFIRMED" ? "green" : "orange",
            }}>
              {team.status}
            </span>
          </li>
        ))}
      </ul>
      <div style={{ marginTop: 16, display: "flex", gap: 8 }}>
        <input
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="Add a note..."
          style={{ flex: 1, padding: 8 }}
        />
        <button
          onClick={async () => {
            await updateNote({
              teamId: entityId,
              input: { namespace: "my-app-data", key: "note", value: note },
            });
            setNote("");
            refetch();
          }}
          style={{ padding: "8px 16px" }}
        >
          Save
        </button>
      </div>
    </div>
  );
}

export default defineReactExtension({
  component: TeamRoster,
});`}
            </GqlBlock>
          </Card>

          <Alert
            icon={<IconInfoCircle />}
            color="yellow"
            variant="light"
            mb="md"
          >
            When using React, make sure your bundler (Vite, webpack, etc.) is
            configured to handle JSX/TSX. The <Code>wellplayed.json</Code>{" "}
            <Code>buildCommand</Code> runs your build before deployment, so any
            standard React build setup will work.
          </Alert>

          <Title order={4} mb="xs">
            Embedding in Liquid Templates
          </Title>
          <Text mb="sm" size="sm">
            Platform administrators embed modules into generated pages using the{" "}
            <Code>module</Code> Liquid tag:
          </Text>
          <Card className={classes.card} p="md" radius="md">
            <GqlBlock>
              {`{% module app-id:"your-app-id" module-name:"leaderboard-widget" title:"Top Players" limit:10 %}`}
            </GqlBlock>
          </Card>

          <Alert
            icon={<IconInfoCircle />}
            color="indigo"
            variant="light"
            mt="md"
          >
            Props passed in the Liquid tag override the default values defined
            in your <Code>propsSchema</Code>. Route params (like{" "}
            <Code>tournamentId</Code>) are automatically injected from the
            current page URL.
          </Alert>
        </div>

        {/* ===================== MODULE UI COMPONENTS ===================== */}
        <div className={classes.section}>
          <SectionHeader
            icon={<IconPuzzle size={18} />}
            title="Module UI Components"
            id="module-ui"
          />
          <Text mb="md">
            <Code>@well-played.gg/module-ui</Code> is a zero-dependency themed
            React component library designed for building marketplace modules.
            It provides a consistent set of UI primitives that automatically
            adapt to the organization&apos;s theme when rendered inside the
            platform.
          </Text>

          <Title order={4} mb="xs">
            Installation
          </Title>
          <Card className={classes.card} p="md" radius="md" mb="md">
            <GqlBlock>{`npm install @well-played.gg/module-ui`}</GqlBlock>
          </Card>

          <Title order={4} mb="xs">
            Available Components
          </Title>
          <Table
            striped
            highlightOnHover
            className={classes.statusTable}
            mb="md"
          >
            <thead>
              <tr>
                <th>Component</th>
                <th>Description</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>
                  <Code>Button</Code>
                </td>
                <td>
                  Primary action button with variants (filled, outline, subtle,
                  ghost) and sizes (xs, sm, md, lg)
                </td>
              </tr>
              <tr>
                <td>
                  <Code>Card</Code>, <Code>CardHeader</Code>,{" "}
                  <Code>CardContent</Code>
                </td>
                <td>
                  Container component with optional header and content sections
                </td>
              </tr>
              <tr>
                <td>
                  <Code>Badge</Code>
                </td>
                <td>
                  Small label for status indicators and tags with color variants
                </td>
              </tr>
              <tr>
                <td>
                  <Code>Avatar</Code>, <Code>AvatarGroup</Code>
                </td>
                <td>
                  User or entity avatar with fallback initials and stacked group
                  display
                </td>
              </tr>
              <tr>
                <td>
                  <Code>Input</Code>
                </td>
                <td>
                  Text input with label, placeholder, error state, and
                  description
                </td>
              </tr>
              <tr>
                <td>
                  <Code>Select</Code>
                </td>
                <td>
                  Dropdown select with options, placeholder, and controlled
                  value
                </td>
              </tr>
              <tr>
                <td>
                  <Code>Modal</Code>, <Code>ModalHeader</Code>,{" "}
                  <Code>ModalBody</Code>, <Code>ModalFooter</Code>
                </td>
                <td>
                  Dialog overlay with composable header, body, and footer
                  sections
                </td>
              </tr>
              <tr>
                <td>
                  <Code>Table</Code>, <Code>TableHead</Code>,{" "}
                  <Code>TableBody</Code>, <Code>TableRow</Code>,{" "}
                  <Code>TableCell</Code>
                </td>
                <td>Structured data table with themed rows and cells</td>
              </tr>
              <tr>
                <td>
                  <Code>Alert</Code>
                </td>
                <td>
                  Contextual feedback message with info, success, warning, and
                  error variants
                </td>
              </tr>
              <tr>
                <td>
                  <Code>Skeleton</Code>
                </td>
                <td>
                  Placeholder loading animation for content that is still
                  fetching
                </td>
              </tr>
              <tr>
                <td>
                  <Code>Tooltip</Code>
                </td>
                <td>Hover-activated contextual information popup</td>
              </tr>
            </tbody>
          </Table>

          <Title order={4} mb="xs">
            Usage Examples
          </Title>
          <Text mb="sm" size="sm">
            Button with variants:
          </Text>
          <Card className={classes.card} p="md" radius="md" mb="md">
            <GqlBlock>
              {`import { Button } from "@well-played.gg/module-ui";

function MyModule() {
  return (
    <div>
      <Button variant="filled" onClick={() => console.log("clicked")}>
        Register
      </Button>
      <Button variant="outline" size="sm">
        Cancel
      </Button>
      <Button variant="subtle" disabled>
        Loading...
      </Button>
    </div>
  );
}`}
            </GqlBlock>
          </Card>

          <Text mb="sm" size="sm">
            Card with header and content:
          </Text>
          <Card className={classes.card} p="md" radius="md" mb="md">
            <GqlBlock>
              {`import { Card, CardHeader, CardContent, Badge } from "@well-played.gg/module-ui";

function TournamentCard({ name, status }) {
  return (
    <Card>
      <CardHeader>
        <h3>{name}</h3>
        <Badge color={status === "ACTIVE" ? "green" : "gray"}>
          {status}
        </Badge>
      </CardHeader>
      <CardContent>
        <p>Tournament details go here.</p>
      </CardContent>
    </Card>
  );
}`}
            </GqlBlock>
          </Card>

          <Text mb="sm" size="sm">
            Modal with composable sections:
          </Text>
          <Card className={classes.card} p="md" radius="md" mb="md">
            <GqlBlock>
              {`import { useState } from "react";
import {
  Modal, ModalHeader, ModalBody, ModalFooter, Button,
} from "@well-played.gg/module-ui";

function ConfirmDialog() {
  const [open, setOpen] = useState(false);

  return (
    <>
      <Button onClick={() => setOpen(true)}>Open Dialog</Button>
      <Modal open={open} onClose={() => setOpen(false)}>
        <ModalHeader>Confirm Action</ModalHeader>
        <ModalBody>
          <p>Are you sure you want to proceed?</p>
        </ModalBody>
        <ModalFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>
            Cancel
          </Button>
          <Button onClick={() => setOpen(false)}>
            Confirm
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
}`}
            </GqlBlock>
          </Card>

          <Title order={4} mb="xs">
            Theming
          </Title>
          <Text mb="sm" size="sm">
            All module-ui components are styled using <Code>--wp-*</Code> CSS
            custom properties. These are automatically injected by the platform
            based on the organization&apos;s theme configuration. You can also
            use these variables in your own custom styles.
          </Text>
          <Table
            striped
            highlightOnHover
            className={classes.statusTable}
            mb="md"
          >
            <thead>
              <tr>
                <th>CSS Variable</th>
                <th>Description</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>
                  <Code>--wp-color-primary</Code>
                </td>
                <td>
                  Primary brand color used for buttons, links, and accents
                </td>
              </tr>
              <tr>
                <td>
                  <Code>--wp-color-background</Code>
                </td>
                <td>Page and card background color</td>
              </tr>
              <tr>
                <td>
                  <Code>--wp-color-surface</Code>
                </td>
                <td>Elevated surface color (cards, modals, dropdowns)</td>
              </tr>
              <tr>
                <td>
                  <Code>--wp-color-text</Code>
                </td>
                <td>Default text color</td>
              </tr>
              <tr>
                <td>
                  <Code>--wp-color-text-muted</Code>
                </td>
                <td>Secondary / dimmed text color</td>
              </tr>
              <tr>
                <td>
                  <Code>--wp-color-border</Code>
                </td>
                <td>Border color for inputs, cards, and dividers</td>
              </tr>
              <tr>
                <td>
                  <Code>--wp-radius</Code>
                </td>
                <td>Default border-radius for components</td>
              </tr>
              <tr>
                <td>
                  <Code>--wp-font-family</Code>
                </td>
                <td>Font family applied to all text</td>
              </tr>
            </tbody>
          </Table>

          <Alert icon={<IconInfoCircle />} color="indigo" variant="light">
            Modules automatically inherit the organization&apos;s theme when
            rendered on a platform page. You do not need to configure or
            initialize the theme manually &mdash; the CSS custom properties are
            injected into the module iframe by the platform runtime.
          </Alert>
        </div>

        {/* ===================== WEB COMPONENTS ===================== */}
        <div className={classes.section}>
          <SectionHeader
            icon={<IconBrackets size={18} />}
            title="Web Components"
            id="web-components"
          />
          <Text mb="md">
            <Code>@well-played.gg/component-sdk</Code> provides a
            framework-agnostic Web Components SDK for building platform widgets
            that run natively in the browser. Components built with this SDK
            work in any frontend framework (React, Vue, Svelte, plain HTML) and
            are rendered using Shadow DOM for style isolation.
          </Text>

          <Title order={4} mb="xs">
            Installation
          </Title>
          <Card className={classes.card} p="md" radius="md" mb="md">
            <GqlBlock>{`npm install @well-played.gg/component-sdk`}</GqlBlock>
          </Card>

          <Title order={4} mb="xs">
            WpElement Base Class
          </Title>
          <Text mb="sm" size="sm">
            All Web Components extend <Code>WpElement</Code>, which provides
            Shadow DOM encapsulation, template rendering with <Code>html</Code>{" "}
            and <Code>css</Code> tagged template literals, reactive props,
            platform bridge access, and built-in GraphQL client methods.
          </Text>
          <Card className={classes.card} p="md" radius="md" mb="md">
            <GqlBlock>
              {`import { WpElement, html, css } from "@well-played.gg/component-sdk";

class TournamentBanner extends WpElement {
  static tag = "wp-tournament-banner";

  static styles = css\`
    :host {
      display: block;
      font-family: var(--wp-font-family, sans-serif);
    }
    .banner {
      padding: 16px;
      background: var(--wp-color-surface);
      border: 1px solid var(--wp-color-border);
      border-radius: var(--wp-radius, 8px);
    }
    .title {
      color: var(--wp-color-text);
      margin: 0 0 8px;
    }
    .status {
      color: var(--wp-color-primary);
      font-weight: 600;
    }
  \`;

  async connectedCallback() {
    super.connectedCallback();

    const { data } = await this.query(
      \`query ($id: String!) {
        tournament(id: $id) { id name status }
      }\`,
      { id: this.props.tournamentId }
    );

    this.render(html\`
      <div class="banner">
        <h3 class="title">\${data.tournament.name}</h3>
        <span class="status">\${data.tournament.status}</span>
      </div>
    \`);
  }
}

customElements.define(TournamentBanner.tag, TournamentBanner);
export default TournamentBanner;`}
            </GqlBlock>
          </Card>

          <Title order={4} mb="xs">
            WpElement Methods
          </Title>
          <Table
            striped
            highlightOnHover
            className={classes.statusTable}
            mb="md"
          >
            <thead>
              <tr>
                <th>Method</th>
                <th>Description</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>
                  <Code>query(query, variables)</Code>
                </td>
                <td>
                  Execute an authenticated GraphQL query using the module token
                </td>
              </tr>
              <tr>
                <td>
                  <Code>mutate(mutation, variables)</Code>
                </td>
                <td>
                  Execute an authenticated GraphQL mutation using the module
                  token
                </td>
              </tr>
              <tr>
                <td>
                  <Code>subscribe(subscription, variables, handler)</Code>
                </td>
                <td>
                  Subscribe to a GraphQL subscription and invoke the handler on
                  each event
                </td>
              </tr>
              <tr>
                <td>
                  <Code>emit(eventName, payload)</Code>
                </td>
                <td>
                  Emit a custom event to sibling modules and the host page
                </td>
              </tr>
              <tr>
                <td>
                  <Code>on(eventName, handler)</Code>
                </td>
                <td>
                  Listen for custom events from sibling modules or the host page
                </td>
              </tr>
              <tr>
                <td>
                  <Code>invalidateQueries()</Code>
                </td>
                <td>Re-fetch all cached queries to refresh displayed data</td>
              </tr>
              <tr>
                <td>
                  <Code>navigate(path)</Code>
                </td>
                <td>
                  Navigate to a different platform page without a full reload
                </td>
              </tr>
            </tbody>
          </Table>

          <Title order={4} mb="xs">
            WpElement Accessors
          </Title>
          <Table
            striped
            highlightOnHover
            className={classes.statusTable}
            mb="md"
          >
            <thead>
              <tr>
                <th>Accessor</th>
                <th>Description</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>
                  <Code>this.props</Code>
                </td>
                <td>
                  Resolved props from the module&apos;s propsSchema (including
                  route params)
                </td>
              </tr>
              <tr>
                <td>
                  <Code>this.theme</Code>
                </td>
                <td>
                  Current organization theme object with color and typography
                  values
                </td>
              </tr>
              <tr>
                <td>
                  <Code>this.user</Code>
                </td>
                <td>
                  Current authenticated user information (id, name, email)
                </td>
              </tr>
              <tr>
                <td>
                  <Code>this.route</Code>
                </td>
                <td>
                  Current route information including path, params, and query
                  string
                </td>
              </tr>
            </tbody>
          </Table>

          <Title order={4} mb="xs">
            PlatformBridge
          </Title>
          <Text mb="sm" size="sm">
            Web Components receive platform capabilities through the{" "}
            <Code>PlatformBridge</Code> &mdash; an injected interface that
            provides the GraphQL client, authentication token, theme, user
            context, and navigation. The bridge is automatically wired when the
            component is registered on a platform page, so you never need to
            initialize it manually.
          </Text>

          <Title order={4} mb="xs">
            Scaffolding
          </Title>
          <Text mb="sm" size="sm">
            Use the scaffold command to generate a new Web Component project
            with all the boilerplate:
          </Text>
          <Card className={classes.card} p="md" radius="md" mb="md">
            <GqlBlock>{`npx @well-played.gg/create-component my-widget`}</GqlBlock>
          </Card>
          <Text mb="sm" size="sm">
            Generated file structure:
          </Text>
          <Card className={classes.card} p="md" radius="md" mb="md">
            <GqlBlock>
              {`my-widget/
├── manifest.json           # Component manifest
├── wellplayed.json         # CLI config
├── package.json
├── tsconfig.json
├── vite.config.ts
└── src/
    ├── index.ts            # Entry point & custom element registration
    └── MyWidget.ts         # WpElement component class`}
            </GqlBlock>
          </Card>

          <Alert icon={<IconInfoCircle />} color="indigo" variant="light">
            Web Components built with the component SDK are framework-agnostic.
            They work in React, Vue, Svelte, or plain HTML pages. Use Web
            Components when you need maximum portability or want to avoid
            shipping a framework runtime inside your bundle.
          </Alert>
        </div>

        {/* ===================== SDK PACKAGES REFERENCE ===================== */}
        <div className={classes.section}>
          <SectionHeader
            icon={<IconListDetails size={18} />}
            title="SDK Packages Reference"
            id="sdk-packages"
          />
          <Text mb="md">
            The WellPlayed developer ecosystem provides a set of npm packages
            for building, theming, and deploying marketplace apps, modules, and
            components. Below is the complete list of available packages.
          </Text>

          <Table
            striped
            highlightOnHover
            className={classes.statusTable}
            mb="md"
          >
            <thead>
              <tr>
                <th>Package</th>
                <th>Description</th>
                <th>Install</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>
                  <Code>@well-played.gg/typescript-sdk</Code>
                </td>
                <td>
                  Core TypeScript SDK with typed GraphQL client, authentication
                  helpers, and shared utilities
                </td>
                <td>
                  <Code>npm install @well-played.gg/typescript-sdk</Code>
                </td>
              </tr>
              <tr>
                <td>
                  <Code>@well-played.gg/react-sdk</Code>
                </td>
                <td>
                  React bindings with hooks for authentication, GraphQL queries,
                  and platform context
                </td>
                <td>
                  <Code>npm install @well-played.gg/react-sdk</Code>
                </td>
              </tr>
              <tr>
                <td>
                  <Code>@well-played.gg/module-sdk</Code>
                </td>
                <td>
                  Module and extension SDK with lifecycle hooks, event system,
                  and React integration
                </td>
                <td>
                  <Code>npm install @well-played.gg/module-sdk</Code>
                </td>
              </tr>
              <tr>
                <td>
                  <Code>@well-played.gg/module-ui</Code>
                </td>
                <td>
                  Zero-dependency themed React component library (Button, Card,
                  Modal, Table, etc.)
                </td>
                <td>
                  <Code>npm install @well-played.gg/module-ui</Code>
                </td>
              </tr>
              <tr>
                <td>
                  <Code>@well-played.gg/component-sdk</Code>
                </td>
                <td>
                  Framework-agnostic Web Components SDK with WpElement base
                  class and Shadow DOM
                </td>
                <td>
                  <Code>npm install @well-played.gg/component-sdk</Code>
                </td>
              </tr>
              <tr>
                <td>
                  <Code>@well-played.gg/create-module</Code>
                </td>
                <td>
                  Scaffolding tool that generates a new module project with
                  boilerplate and manifest
                </td>
                <td>
                  <Code>npx @well-played.gg/create-module my-module</Code>
                </td>
              </tr>
              <tr>
                <td>
                  <Code>@well-played.gg/create-component</Code>
                </td>
                <td>
                  Scaffolding tool that generates a new Web Component project
                  with WpElement setup
                </td>
                <td>
                  <Code>npx @well-played.gg/create-component my-widget</Code>
                </td>
              </tr>
              <tr>
                <td>
                  <Code>@well-played.gg/cli</Code>
                </td>
                <td>
                  Command-line interface for authentication, local development
                  server, and deployment
                </td>
                <td>
                  <Code>npm install -g @well-played.gg/cli</Code>
                </td>
              </tr>
            </tbody>
          </Table>
        </div>

        {/* ===================== BUILDING WITH REACT ===================== */}
        <div className={classes.section}>
          <SectionHeader
            icon={<IconCode size={18} />}
            title="Building with React"
            id="building-with-react"
          />
          <Text mb="md">
            React is the recommended way to build extensions and modules on the
            WellPlayed platform. The module SDK provides first-class React
            support with hooks for context, GraphQL, and inter-module
            communication.
          </Text>

          <Title order={4} mb="xs">
            Project Setup
          </Title>
          <Text mb="sm" size="sm">
            Scaffold a React-based app project with Vite and install the
            required dependencies:
          </Text>
          <Card className={classes.card} p="md" radius="md" mb="md">
            <GqlBlock>
              {`# Scaffold project
wellplayed init my-react-app
cd my-react-app

# Install React and the module SDK
npm install react react-dom @well-played.gg/module-sdk

# Install dev dependencies for TypeScript + JSX
npm install -D @types/react @types/react-dom typescript vite @vitejs/plugin-react`}
            </GqlBlock>
          </Card>

          <Title order={4} mb="xs">
            Vite Configuration
          </Title>
          <Text mb="sm" size="sm">
            Configure Vite to build your extensions and modules as library
            bundles:
          </Text>
          <Card className={classes.card} p="md" radius="md" mb="md">
            <GqlBlock>
              {`// vite.config.ts
import { defineConfig } from "vite";
import react from "@vitejs/plugin-react";

export default defineConfig({
  plugins: [react()],
  build: {
    lib: {
      entry: {
        // Each extension/module gets its own entry point
        "extensions/tournament-analytics": "src/extensions/tournament-analytics.tsx",
        "modules/leaderboard-widget": "src/modules/leaderboard-widget.tsx",
      },
      formats: ["es"],
    },
    rollupOptions: {
      // React is provided by the module SDK runtime
      external: ["react", "react-dom"],
    },
  },
});`}
            </GqlBlock>
          </Card>

          <Title order={4} mb="xs">
            wellplayed.json for React Projects
          </Title>
          <Card className={classes.card} p="md" radius="md" mb="md">
            <GqlBlock>
              {`{
  "appId": "your-app-id",
  "buildCommand": "npm run build",
  "outputDir": "dist",
  "devPort": 3000,
  "manifestPath": "manifest.json"
}`}
            </GqlBlock>
          </Card>

          <Title order={4} mb="xs">
            Project Structure
          </Title>
          <Card className={classes.card} p="md" radius="md" mb="md">
            <GqlBlock>
              {`my-react-app/
├── manifest.json           # App manifest (extensions, modules, permissions)
├── wellplayed.json         # CLI config
├── vite.config.ts          # Build configuration
├── tsconfig.json
├── package.json
└── src/
    ├── extensions/
    │   ├── tournament-analytics.tsx    # Extension entry point
    │   └── TournamentAnalytics.tsx     # React component
    └── modules/
        ├── leaderboard-widget.tsx      # Module entry point
        └── LeaderboardWidget.tsx       # React component`}
            </GqlBlock>
          </Card>

          <Title order={4} mb="xs">
            Quick Reference: React APIs
          </Title>
          <Table
            striped
            highlightOnHover
            className={classes.statusTable}
            mb="md"
          >
            <thead>
              <tr>
                <th>API</th>
                <th>Purpose</th>
                <th>Use In</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>
                  <Code>defineReactModule()</Code>
                </td>
                <td>Declare a React module with props schema and component</td>
                <td>Modules</td>
              </tr>
              <tr>
                <td>
                  <Code>defineReactExtension()</Code>
                </td>
                <td>Declare a React extension (handles mount/unmount)</td>
                <td>Extensions</td>
              </tr>
              <tr>
                <td>
                  <Code>useModuleContext()</Code>
                </td>
                <td>Access props, organizationId, token, entityId</td>
                <td>Both</td>
              </tr>
              <tr>
                <td>
                  <Code>useModuleQuery()</Code>
                </td>
                <td>Execute authenticated GraphQL queries</td>
                <td>Both</td>
              </tr>
              <tr>
                <td>
                  <Code>useModuleMutation()</Code>
                </td>
                <td>Execute authenticated GraphQL mutations</td>
                <td>Both</td>
              </tr>
              <tr>
                <td>
                  <Code>useModuleEvent()</Code>
                </td>
                <td>Listen for events from other modules</td>
                <td>Both</td>
              </tr>
              <tr>
                <td>
                  <Code>useModuleEmit()</Code>
                </td>
                <td>Emit events to other modules</td>
                <td>Both</td>
              </tr>
            </tbody>
          </Table>

          <Alert icon={<IconInfoCircle />} color="indigo" variant="light">
            All React hooks are imported from{" "}
            <Code>@well-played.gg/module-sdk/react</Code>. The SDK provides the
            React runtime in the sandboxed iframe, so you don&apos;t need to
            worry about React version conflicts with the host platform.
          </Alert>
        </div>

        {/* ===================== RELEASE WORKFLOW ===================== */}
        <div className={classes.section}>
          <SectionHeader
            icon={<IconWorldUpload size={18} />}
            title="Release Workflow"
            id="release-workflow"
          />
          <Text mb="md">
            Once your app is ready, create a release to submit it for review and
            publish it to the marketplace. Releases bundle your extension and
            module source files and go through a review process before becoming
            available.
          </Text>

          <Card className={classes.card} p="md" radius="md" mb="md">
            <Text fw={600} mb="xs" size="sm">
              Release Lifecycle
            </Text>
            <GqlBlock>
              {`  Create           Upload          Finalize        Review          Publish
┌──────────┐  ┌──────────┐  ┌──────────┐  ┌──────────┐  ┌──────────┐
│  DRAFT   │─→│  DRAFT   │─→│ PENDING  │─→│ APPROVED │─→│PUBLISHED │
│          │  │ + assets │  │  REVIEW  │  │          │  │          │
└──────────┘  └──────────┘  └──────────┘  └──────────┘  └──────────┘
                                               │
                                               v
                                          ┌──────────┐
                                          │ REJECTED │
                                          │(fix+retry)│
                                          └──────────┘`}
            </GqlBlock>
          </Card>

          <Accordion variant="separated" mb="md">
            <Accordion.Item value="create-release">
              <Accordion.Control>
                <Group gap="xs">
                  <Badge color="cyan" className={classes.mutationBadge}>
                    Step 1
                  </Badge>
                  <Text fw={500}>Create a Release</Text>
                </Group>
              </Accordion.Control>
              <Accordion.Panel>
                <Text size="sm" mb="xs">
                  Create a release from the Developer console or using the CLI.
                  The release starts in <Code>DRAFT</Code> status.
                </Text>
                <GqlBlock>
                  {`# Via CLI (recommended)
wellplayed deploy --app-id <your-app-id>

# This automatically:
# 1. Runs your build command
# 2. Creates a draft release
# 3. Uploads bundled assets
# 4. Finalizes the release for review`}
                </GqlBlock>
              </Accordion.Panel>
            </Accordion.Item>

            <Accordion.Item value="upload-bundles">
              <Accordion.Control>
                <Group gap="xs">
                  <Badge color="cyan" className={classes.mutationBadge}>
                    Step 2
                  </Badge>
                  <Text fw={500}>Upload Bundles</Text>
                </Group>
              </Accordion.Control>
              <Accordion.Panel>
                <Text size="sm" mb="xs">
                  The CLI bundles your source files during deploy. If deploying
                  manually, upload the following files:
                </Text>
                <List size="sm" spacing="xs">
                  <List.Item>
                    <Code>extension.js</Code> — Bundled extension point sources
                  </List.Item>
                  <List.Item>
                    <Code>module.js</Code> — Bundled frontend module sources
                  </List.Item>
                  <List.Item>
                    <Code>manifest.json</Code> — The app manifest (uploaded
                    automatically)
                  </List.Item>
                </List>
              </Accordion.Panel>
            </Accordion.Item>

            <Accordion.Item value="review-process">
              <Accordion.Control>
                <Group gap="xs">
                  <Badge color="cyan" className={classes.mutationBadge}>
                    Step 3
                  </Badge>
                  <Text fw={500}>Review Process</Text>
                </Group>
              </Accordion.Control>
              <Accordion.Panel>
                <Text size="sm" mb="xs">
                  After finalization, the release enters{" "}
                  <Code>PENDING_REVIEW</Code>. The WellPlayed team reviews your
                  submission for:
                </Text>
                <List size="sm" spacing="xs">
                  <List.Item>
                    Security — no malicious code, proper scope usage
                  </List.Item>
                  <List.Item>
                    Performance — bundle size limits, no excessive API calls
                  </List.Item>
                  <List.Item>
                    Quality — proper error handling, responsive UI
                  </List.Item>
                  <List.Item>
                    Policy — compliance with marketplace guidelines
                  </List.Item>
                </List>
                <Alert
                  icon={<IconInfoCircle />}
                  color="indigo"
                  variant="light"
                  mt="sm"
                  p="xs"
                >
                  <Text size="xs">
                    If your release is rejected, you will receive feedback
                    explaining the issues. Fix the problems and create a new
                    release to resubmit.
                  </Text>
                </Alert>
              </Accordion.Panel>
            </Accordion.Item>

            <Accordion.Item value="publish">
              <Accordion.Control>
                <Group gap="xs">
                  <Badge color="cyan" className={classes.mutationBadge}>
                    Step 4
                  </Badge>
                  <Text fw={500}>Publishing</Text>
                </Group>
              </Accordion.Control>
              <Accordion.Panel>
                <Text size="sm">
                  Once approved, the release is published and becomes available
                  to organizations in the marketplace. Organizations can install
                  your app from the marketplace page. Previously published
                  releases are automatically replaced by the new version.
                </Text>
              </Accordion.Panel>
            </Accordion.Item>
          </Accordion>
        </div>

        {/* ===================== EXISTING SECTIONS BELOW ===================== */}

        <Divider
          label="API & Integration Reference"
          labelPosition="center"
          mb="md"
        />

        {/* ===================== OVERVIEW ===================== */}
        <div className={classes.section}>
          <SectionHeader
            icon={<IconApi size={18} />}
            title="API Overview"
            id="overview"
          />
          <Text mb="md">
            The Apps & Webhooks system provides OAuth2-based authentication for
            third-party applications and real-time event notifications through
            webhooks. Organization apps act as OAuth2 clients that can
            authenticate users on your behalf, while webhooks deliver event
            payloads to your server whenever specific actions occur in the
            system.
          </Text>
          <Text mb="md">
            The system is composed of the following components:
          </Text>
          <Card className={classes.card} p="md" radius="md">
            <GqlBlock>
              {`Apps & Webhooks
├── Authentication (OAuth2 / OpenID Connect)
│   ├── Authorization endpoint
│   ├── Token endpoint
│   ├── UserInfo endpoint
│   └── Logout / Revocation endpoints
├── Organization Apps[] (OAuth2 clients)
│   ├── Configuration (redirect URLs, consent, login URLs)
│   ├── Secret (for authenticating API requests)
│   └── Attached Apps (apps linked to permission groups)
└── Webhooks[] (event subscriptions)
    ├── URL (HTTP POST target)
    ├── Events[] (subscribed event types)
    ├── Secret (for signature verification)
    └── Available Events (dynamic payload schemas)`}
            </GqlBlock>
          </Card>

          <Alert
            icon={<IconInfoCircle />}
            color="indigo"
            variant="light"
            mt="md"
          >
            API requests from OAuth2 apps must include an{" "}
            <Code>organization-id</Code> header and a <Code>Bearer</Code>{" "}
            authorization token. Marketplace module extensions use a single{" "}
            <Code>x-module-token</Code> header instead — see the Extensions
            section above.
          </Alert>
        </div>

        {/* ===================== AUTHENTICATION ===================== */}
        <div className={classes.section}>
          <SectionHeader
            icon={<IconLock size={18} />}
            title="Authentication"
            id="authentication"
          />
          <Text mb="md">
            WellPlayed uses OAuth2 and OpenID Connect (OIDC) as its
            authentication mechanism. All API calls require a valid
            authorization token obtained through the OAuth2 flow.
          </Text>

          <Title order={4} mb="xs">
            Request Format
          </Title>
          <Text mb="sm">
            To authenticate API requests, include the authorization token and
            organization ID in the request headers:
          </Text>
          <Card className={classes.card} p="md" radius="md" mb="md">
            <GqlBlock>
              {`curl -X POST \\
  -H 'organization-id: ORGANIZATION_ID_HERE' \\
  -H 'authorization: Bearer TOKEN_HERE' \\
  -d '{"query": "{ ... }"}' \\
  https://api.warrior.well-played.gg/graphql`}
            </GqlBlock>
          </Card>

          <Title order={4} mb="xs">
            OpenID Configuration
          </Title>
          <Text mb="sm">
            The OpenID configuration endpoint can be used to automatically
            configure OIDC clients:
          </Text>
          <Card className={classes.card} p="md" radius="md" mb="md">
            <Code>
              https://oauth.warrior.well-played.gg/.well-known/openid-configuration
            </Code>
          </Card>

          <Title order={4} mb="xs">
            Example Integration (React SDK)
          </Title>
          <Text mb="sm">
            The recommended path is{" "}
            <Code>@well-played.gg/react-sdk</Code>, which wires Apollo Client,
            the typed gql.tada client, organization headers, OIDC token
            management, and silent refresh in a single provider:
          </Text>
          <Card className={classes.card} p="md" radius="md" mb="md">
            <GqlBlock>
              {`import { WellPlayedProvider } from "@well-played.gg/react-sdk";

<WellPlayedProvider
  organizationId="YOUR_ORGANIZATION_SHORT_ID"
  wpAppConfig={{
    client_id: "YOUR_APP_CLIENT_ID",
    redirect_uri: \`\${window.location.origin}/login\`,
    scope: "offline_access",          // REQUIRED for refresh tokens
  }}
  apiBaseUrl="well-played.gg"          // or "stg.well-played.gg"
>
  <App />
</WellPlayedProvider>`}
            </GqlBlock>
          </Card>
          <Text mb="sm" size="sm">
            Inside the tree, use the hooks exposed by the SDK:
          </Text>
          <Card className={classes.card} p="md" radius="md" mb="md">
            <GqlBlock>
              {`import {
  useConnectedPlayer,
  useWellPlayed,
} from "@well-played.gg/react-sdk";

const Header = () => {
  const { authenticated, data, login, logout } = useConnectedPlayer();
  if (!authenticated) {
    return <button onClick={() => login("/dashboard")}>Sign in</button>;
  }
  return (
    <>
      <span>Hi {data?.getMyAccount.profiles[0]?.username}</span>
      <button onClick={() => logout()}>Sign out</button>
    </>
  );
};

const CustomQuery = () => {
  // Apollo client + typed gql.tada client + current access token + org id
  const { apiClient, typedClient, accessToken, organizationId } = useWellPlayed();
  // ...
};`}
            </GqlBlock>
          </Card>
          <Text mb="sm" size="sm">
            For non-React clients, use{" "}
            <Code>@well-played.gg/typescript-sdk</Code> (
            <Code>createWellPlayedClient</Code>,{" "}
            <Code>createTypedClient</Code>) which exposes the same Apollo /
            gql.tada wiring without React.
          </Text>
          <Alert icon={<IconAlertCircle />} color="yellow" variant="light">
            Always pass <Code>scope: &quot;offline_access&quot;</Code>. Without
            it, no refresh token is issued and the user is bounced back to
            login as soon as the access token expires.
          </Alert>
        </div>

        {/* ===================== TOKEN TYPES ===================== */}
        <div className={classes.section}>
          <SectionHeader
            icon={<IconKey size={18} />}
            title="Token Types"
            id="token-types"
          />
          <Text mb="md">
            The platform issues and accepts three distinct token types
            depending on what is calling the API. They are all sent to{" "}
            <Code>POST https://api.warrior.well-played.gg/graphql</Code> but
            use different headers and have different lifetimes and permission
            ceilings.
          </Text>

          <Title order={4} mb="xs">
            1. User access token (Bearer)
          </Title>
          <Text mb="sm" size="sm">
            Issued by the OAuth2 authorization-code flow on behalf of an
            end-user. JWT (RS256). Sent as{" "}
            <Code>Authorization: Bearer &lt;token&gt;</Code> together with{" "}
            <Code>organization-id: &lt;short-id&gt;</Code>. Refresh handled
            automatically by the React SDK when the{" "}
            <Code>offline_access</Code> scope is requested. This is what every
            front-end and most server-side integrations use.
          </Text>

          <Title order={4} mb="xs">
            2. Application token (client_credentials)
          </Title>
          <Text mb="sm" size="sm">
            Server-to-server token issued via the{" "}
            <Code>client_credentials</Code> grant with the app&apos;s{" "}
            <Code>client_id</Code> + <Code>client_secret</Code>. JWT (RS256).
            Same <Code>Authorization: Bearer</Code> header. Use this from
            backend jobs, schedulers, the CLI, or anything that does not act
            on behalf of a specific user. The TypeScript SDK supports it
            natively:
          </Text>
          <Card className={classes.card} p="md" radius="md" mb="md">
            <GqlBlock>
              {`import { createWellPlayedClient } from "@well-played.gg/typescript-sdk";

const client = createWellPlayedClient({
  organizationId: "YOUR_ORG_SHORT_ID",
  application: {
    clientId: process.env.WP_CLIENT_ID!,
    clientSecret: process.env.WP_CLIENT_SECRET!,
  },
  ssr: true,                          // disables localStorage cache persistence
});`}
            </GqlBlock>
          </Card>
          <Alert
            icon={<IconAlertCircle />}
            color="yellow"
            variant="light"
            mb="md"
          >
            Confidential clients only — never ship a client secret in browser
            code. If a secret is exposed, regenerate it immediately via{" "}
            <Code>resetOrganizationAppSecret</Code> (see Organization Apps
            below).
          </Alert>

          <Title order={4} mb="xs">
            3. Module token (<Code>x-module-token</Code>)
          </Title>
          <Text mb="sm" size="sm">
            Short-lived JWT (HS256, 15-minute TTL) issued automatically by the
            host platform when a marketplace-installed module iframe boots.
            Sent as <Code>x-module-token: &lt;jwt&gt;</Code>{" "}
            <strong>instead of</strong> <Code>Authorization</Code> — the two
            headers are mutually exclusive and the API will reject requests
            that send both. Module tokens carry a permission ceiling: the
            effective permissions are the intersection of the host
            account&apos;s permissions and the{" "}
            <Code>grantedPermissions</Code> set when the module was installed.
            Use <Code>@well-played.gg/module-sdk</Code> to obtain the token
            from the iframe runtime — you should not mint it yourself.
          </Text>

          <Title order={4} mb="xs">
            Header summary
          </Title>
          <Table
            striped
            highlightOnHover
            className={classes.statusTable}
            mb="md"
          >
            <thead>
              <tr>
                <th>Token</th>
                <th>Header(s)</th>
                <th>TTL</th>
                <th>Issued by</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>User access</td>
                <td>
                  <Code>Authorization: Bearer …</Code>
                  <br />
                  <Code>organization-id: …</Code>
                </td>
                <td>~1h (refreshable)</td>
                <td>OAuth2 code flow (login)</td>
              </tr>
              <tr>
                <td>Application</td>
                <td>
                  <Code>Authorization: Bearer …</Code>
                  <br />
                  <Code>organization-id: …</Code>
                </td>
                <td>~1h</td>
                <td>
                  OAuth2 <Code>client_credentials</Code>
                </td>
              </tr>
              <tr>
                <td>Module</td>
                <td>
                  <Code>x-module-token: …</Code>
                </td>
                <td>15 min</td>
                <td>Module SDK runtime (HS256)</td>
              </tr>
            </tbody>
          </Table>
        </div>

        {/* ===================== OAUTH ENDPOINTS ===================== */}
        <div className={classes.section}>
          <SectionHeader
            icon={<IconKey size={18} />}
            title="OAuth Endpoints"
            id="oauth-endpoints"
          />
          <Text mb="md">
            The following endpoints are available for the OAuth2 / OpenID
            Connect flow. These are standard endpoints that can be used with any
            OIDC-compatible client library.
          </Text>

          <Table
            striped
            highlightOnHover
            className={classes.statusTable}
            mb="md"
          >
            <thead>
              <tr>
                <th>Endpoint</th>
                <th>URL</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Authorization</td>
                <td>
                  <Anchor href="https://oauth.warrior.well-played.gg/oauth2/auth">
                    https://oauth.warrior.well-played.gg/oauth2/auth
                  </Anchor>
                </td>
              </tr>
              <tr>
                <td>Token</td>
                <td>
                  <Anchor href="https://oauth.warrior.well-played.gg/oauth2/token">
                    https://oauth.warrior.well-played.gg/oauth2/token
                  </Anchor>
                </td>
              </tr>
              <tr>
                <td>User Info</td>
                <td>
                  <Anchor href="https://oauth.warrior.well-played.gg/userinfo">
                    https://oauth.warrior.well-played.gg/userinfo
                  </Anchor>
                </td>
              </tr>
              <tr>
                <td>End Session</td>
                <td>
                  <Anchor href="https://oauth.warrior.well-played.gg/oauth2/sessions/logout">
                    https://oauth.warrior.well-played.gg/oauth2/sessions/logout
                  </Anchor>
                </td>
              </tr>
              <tr>
                <td>Revocation</td>
                <td>
                  <Anchor href="https://oauth.warrior.well-played.gg/oauth2/revoke">
                    https://oauth.warrior.well-played.gg/oauth2/revoke
                  </Anchor>
                </td>
              </tr>
            </tbody>
          </Table>

          <Card className={classes.card} p="md" radius="md" mb="md">
            <Title order={4} mb="xs">
              Standard authorization code flow
            </Title>
            <GqlBlock>
              {`User clicks "Login"
  |
  v
1. Browser is redirected to the Authorization endpoint with
   client_id, redirect_uri, scope=offline_access, state, PKCE
  |
  v
2. WellPlayed orchestrates the login (see "Login Flows" below for what
   actually happens between steps 2 and 3 — it depends on how the
   organization is configured)
  |
  v
3. Browser is redirected back to redirect_uri with ?code=…&state=…
  |
  v
4. App exchanges code at the Token endpoint for an access_token
   (and refresh_token, if offline_access was requested)
  |
  v
5. App sends Authorization: Bearer <access_token> + organization-id: <id>
   on every GraphQL request`}
            </GqlBlock>
          </Card>

          <Alert icon={<IconInfoCircle />} color="indigo" variant="light">
            The <Code>offline_access</Code> scope is required to obtain a
            refresh token, which allows your application to maintain long-lived
            sessions without requiring the user to re-authenticate.
          </Alert>
        </div>

        {/* ===================== LOGIN FLOWS ===================== */}
        <div className={classes.section}>
          <SectionHeader
            icon={<IconLock size={18} />}
            title="Login Flows"
            id="login-flows"
          />
          <Text mb="md">
            Step 2 of the standard flow above (&quot;WellPlayed orchestrates
            the login&quot;) actually branches into one of three paths based on
            how the target organization has configured its identity providers.
            All three converge on the same authorization-code redirect to your{" "}
            <Code>redirect_uri</Code>, so your application code does not need
            to differentiate — but understanding which flow your users will
            see helps when configuring identity providers and writing tenant
            login UIs.
          </Text>

          <Accordion variant="separated" mb="md">
            <Accordion.Item value="single-provider">
              <Accordion.Control>
                <Group gap="xs">
                  <IconKey size={16} />
                  <Text fw={500}>
                    Flow A — Single-provider tenant SSO (auto-redirect)
                  </Text>
                </Group>
              </Accordion.Control>
              <Accordion.Panel>
                <Text size="sm" mb="sm">
                  Triggered when the organization has{" "}
                  <strong>exactly one</strong> identity provider configured
                  with <Code>allowLogin = true</Code>. The user is redirected
                  straight to that provider — no picker UI is shown.
                </Text>
                <Card className={classes.card} p="md" radius="md">
                  <GqlBlock>
                    {`Browser → /oauth2/auth?client_id=…
       → WellPlayed mints login_challenge L
       → 302 → /login-redirect?login_challenge=L
       → Looks up the org's providers, finds exactly 1
       → 302 → external IdP /authorize?...&state=L
External IdP → user authenticates
       → 302 → /login/{providerId}?code=C&state=L
       → Token exchange + identity retrieval
       → Find or create Account + AccountIdentity
       → acceptLoginRequest(L, accountId)
       → 302 → consent flow → your redirect_uri?code=AC`}
                  </GqlBlock>
                </Card>
              </Accordion.Panel>
            </Accordion.Item>

            <Accordion.Item value="multi-provider">
              <Accordion.Control>
                <Group gap="xs">
                  <IconBrackets size={16} />
                  <Text fw={500}>
                    Flow B — Multi-provider tenant SSO (provider picker)
                  </Text>
                </Group>
              </Accordion.Control>
              <Accordion.Panel>
                <Text size="sm" mb="sm">
                  Triggered when the organization has{" "}
                  <strong>two or more</strong> identity providers with{" "}
                  <Code>allowLogin = true</Code>. WellPlayed cannot pick for
                  the user, so it bounces them to the tenant&apos;s{" "}
                  <Code>loginUrl</Code> (configured on the OAuth2 client) to
                  render a provider picker. The tenant UI then drives the
                  external IdP dance and POSTs the resulting authorization
                  code back to <Code>/login/{`{providerId}`}</Code>.
                </Text>
                <Card className={classes.card} p="md" radius="md">
                  <GqlBlock>
                    {`Browser → /oauth2/auth → 302 → /login-redirect?login_challenge=L
       → Org has 2+ providers
       → 302 → tenant.loginUrl?login_challenge=L
TenantUI: render picker buttons (Google, Discord, custom OIDC, …)
       → user clicks a provider
       → tenant builds authorize URL itself, gets code C
       → POST /login/{providerId} { challenge:L, token:C, remember }
       → same Account / AccountIdentity logic as Flow A
       → response { redirectTo } → 302 → consent → redirect_uri`}
                  </GqlBlock>
                </Card>
                <Text size="sm" mt="sm">
                  Configure <Code>loginUrl</Code> on the Organization App
                  (see Organization Apps below) to point at your tenant&apos;s
                  branded login page.
                </Text>
              </Accordion.Panel>
            </Accordion.Item>

            <Accordion.Item value="callback-bridge">
              <Accordion.Control>
                <Group gap="xs">
                  <IconApi size={16} />
                  <Text fw={500}>Flow C — OAuth2 callback bridge</Text>
                </Group>
              </Accordion.Control>
              <Accordion.Panel>
                <Text size="sm" mb="sm">
                  Most external identity providers only support a GET redirect
                  to deliver the authorization code. Flow A handles this
                  transparently via{" "}
                  <Code>GET /login/{`{providerId}`}?code=&amp;state=</Code>{" "}
                  which wraps the equivalent POST. You only need to be aware
                  of this if you are writing a tenant UI that wants to
                  intercept the callback (in which case use Flow B&apos;s
                  POST endpoint directly).
                </Text>
              </Accordion.Panel>
            </Accordion.Item>
          </Accordion>

          <Title order={4} mb="xs">
            Account creation on first login
          </Title>
          <Text mb="sm" size="sm">
            When a user logs in via an identity provider for the first time
            and no <Code>AccountIdentity</Code> matches their external{" "}
            <Code>sub</Code> on that provider, the platform automatically
            creates a new <Code>Account</Code> and the corresponding{" "}
            <Code>AccountIdentity</Code> in a single transaction. The new
            account is enrolled in the organization that owns the identity
            provider, subject to that organization&apos;s{" "}
            <Code>REGISTERED_USERS</Code> quota.
          </Text>
          <Alert
            icon={<IconAlertCircle />}
            color="yellow"
            variant="light"
            mb="md"
          >
            <strong>There is no email-based account merging.</strong> If a
            user logs in via Google with the same email they previously used
            via Discord, you will get two distinct accounts. To consolidate,
            use the explicit linking flow described in{" "}
            <Anchor href="#identity-linking">Identity Linking</Anchor>.
          </Alert>
        </div>

        {/* ===================== IDENTITY LINKING ===================== */}
        <div className={classes.section}>
          <SectionHeader
            icon={<IconPuzzle size={18} />}
            title="Identity Linking"
            id="identity-linking"
          />
          <Text mb="md">
            Linking lets an already-authenticated user attach an additional
            identity provider to their existing account, so they can later log
            in via that provider too. There are three ways an{" "}
            <Code>AccountIdentity</Code> ends up in the database — only one
            of them is initiated by your application.
          </Text>

          <Accordion variant="separated" mb="md">
            <Accordion.Item value="link-implicit">
              <Accordion.Control>
                <Group gap="xs">
                  <IconKey size={16} />
                  <Text fw={500}>
                    Mechanism 1 — Implicit linking on first login
                  </Text>
                </Group>
              </Accordion.Control>
              <Accordion.Panel>
                <Text size="sm">
                  This is the &quot;account creation on first login&quot;
                  branch described above. The new <Code>Account</Code> and
                  its first <Code>AccountIdentity</Code> are created together
                  — there is nothing to call.
                </Text>
              </Accordion.Panel>
            </Accordion.Item>

            <Accordion.Item value="link-explicit">
              <Accordion.Control>
                <Group gap="xs">
                  <IconPuzzle size={16} />
                  <Text fw={500}>
                    Mechanism 2 — Explicit user-initiated link
                    (recommended)
                  </Text>
                </Group>
              </Accordion.Control>
              <Accordion.Panel>
                <Text size="sm" mb="sm">
                  The canonical &quot;Link your Google account&quot; button.
                  Three steps: a GraphQL mutation that returns a one-shot
                  link URL, the browser visiting that URL (which bounces
                  through the external provider), and a final redirect back
                  to your <Code>linkRedirectUrl</Code> with{" "}
                  <Code>?success=true|false</Code>.
                </Text>

                <Title order={5} mb="xs">
                  Step 1 — Mint a link URL
                </Title>
                <Text size="sm" mb="xs">
                  Call <Code>generateIdentityLinkUrl</Code> from the
                  authenticated session. The mutation returns a URL with a
                  5-minute single-use token bound to your account, your
                  organization, and the chosen provider.
                </Text>
                <Card className={classes.card} p="md" radius="md" mb="md">
                  <GqlBlock>
                    {`mutation LinkIdentity($identityProviderId: ID!) {
  generateIdentityLinkUrl(identityProviderId: $identityProviderId) {
    url
  }
}

# Response
# { "data": { "generateIdentityLinkUrl": {
#     "url": "https://oauth.warrior.well-played.gg/link-redirect?linkToken=…&providerId=…"
# } } }`}
                  </GqlBlock>
                </Card>
                <Alert
                  icon={<IconAlertCircle />}
                  color="yellow"
                  variant="light"
                  mb="md"
                  p="xs"
                >
                  <Text size="xs">
                    The target identity provider <strong>must</strong> have a{" "}
                    <Code>linkRedirectUrl</Code> in its configuration,
                    otherwise the mutation throws{" "}
                    <Code>BAD_CONFIGURATION</Code>. That URL is where the
                    user is sent at the end of the flow with the success or
                    failure query string.
                  </Text>
                </Alert>
                <Alert
                  icon={<IconAlertCircle />}
                  color="yellow"
                  variant="light"
                  mb="md"
                  p="xs"
                >
                  <Text size="xs">
                    The authenticated account <strong>must belong to the
                    same organization</strong> as the identity provider.
                    Cross-organization accounts and root accounts (which
                    belong to no organization) are rejected with{" "}
                    <Code>ACCOUNT_NOT_IN_ORGANIZATION</Code> — both when
                    minting the link URL and when the callback finalizes the
                    link.
                  </Text>
                </Alert>

                <Title order={5} mb="xs">
                  Step 2 — Send the user to the URL
                </Title>
                <Text size="sm" mb="xs">
                  Open the returned <Code>url</Code> in the same browser
                  tab. WellPlayed validates the link token, builds the
                  external provider&apos;s authorize URL, and 302s the user
                  to the provider for authentication.
                </Text>

                <Title order={5} mb="xs">
                  Step 3 — Receive the result on linkRedirectUrl
                </Title>
                <Text size="sm" mb="xs">
                  After the user authenticates with the external provider,
                  WellPlayed exchanges the authorization code, upserts the{" "}
                  <Code>AccountIdentity</Code> row, and 302s the user to
                  your <Code>linkRedirectUrl</Code> with one of:
                </Text>
                <Card className={classes.card} p="md" radius="md" mb="sm">
                  <GqlBlock>
                    {`# Success
?success=true

# External provider error
?success=false&error=identity_provider_error

# Identity already linked to a different account
?success=false&error=identity_already_attached`}
                  </GqlBlock>
                </Card>
                <Text size="sm" mb="xs">
                  The <Code>identity_already_attached</Code> case enforces
                  the database uniqueness constraint{" "}
                  <Code>(provider, externalId)</Code>: the same Google
                  account cannot be linked to two WellPlayed accounts on the
                  same provider configuration.
                </Text>
                <Alert
                  icon={<IconInfoCircle />}
                  color="indigo"
                  variant="light"
                  p="xs"
                  mt="xs"
                >
                  <Text size="xs">
                    <strong>Query-string safety.</strong> Params are appended
                    with <Code>&</Code> when the configured{" "}
                    <Code>linkRedirectUrl</Code> already contains a query
                    string (e.g.{" "}
                    <Code>https://myapp.com/cb?ref=1</Code> becomes{" "}
                    <Code>…/cb?ref=1&success=true</Code>).
                    No double-<Code>?</Code> is ever produced.
                  </Text>
                </Alert>
              </Accordion.Panel>
            </Accordion.Item>

            <Accordion.Item value="link-refresh">
              <Accordion.Control>
                <Group gap="xs">
                  <IconRocket size={16} />
                  <Text fw={500}>
                    Mechanism 3 — Refresh on every subsequent login
                  </Text>
                </Group>
              </Accordion.Control>
              <Accordion.Panel>
                <Text size="sm" mb="sm">
                  Every time an existing user logs in via a provider they are
                  already linked to, the platform refreshes their{" "}
                  <Code>refreshToken</Code> and <strong>wipes and
                  recreates</strong> all of their{" "}
                  <Code>AccountIdentityProperty</Code> rows from the
                  provider&apos;s latest userinfo / data-retriever output.
                </Text>
                <Alert
                  icon={<IconAlertCircle />}
                  color="yellow"
                  variant="light"
                  p="xs"
                >
                  <Text size="xs">
                    Do not store user-edited data in identity properties — it
                    will be overwritten on the next login. Persist editable
                    profile data on <Code>PlayerProfile</Code> custom fields
                    instead.
                  </Text>
                </Alert>
              </Accordion.Panel>
            </Accordion.Item>
          </Accordion>

          <Title order={4} mb="xs">
            Linking constraints
          </Title>
          <List size="sm" spacing="xs" mb="md">
            <List.Item>
              <strong>One identity per provider per account.</strong> A given
              account can hold at most one <Code>AccountIdentity</Code> for a
              given provider configuration.
            </List.Item>
            <List.Item>
              <strong>One account per external identity per provider.</strong>{" "}
              The same external <Code>sub</Code> cannot belong to two
              accounts; this is what blocks account hijacking via the link
              flow.
            </List.Item>
            <List.Item>
              <strong>The link token is single-use.</strong> The callback
              endpoint deletes the Redis session as it consumes it, so a
              tampered or replayed <Code>state</Code> fails with HTTP 400.
            </List.Item>
            <List.Item>
              <strong>5-minute window.</strong> The user has 5 minutes to
              complete the external authentication after the mutation
              returns; after that, the link token expires and you must mint a
              new one.
            </List.Item>
          </List>

          <Title order={4} mb="xs">
            Unlinking an identity{" "}
            <Badge color="teal" variant="light" size="sm" ml="xs">
              New
            </Badge>
          </Title>
          <Text size="sm" mb="sm">
            Use the <Code>deleteAccountIdentity</Code> mutation to remove a
            linked identity. This is available as a self-service action (a
            user removing one of their own linked providers) and as an admin
            action (removing any account's identity with an explicit{" "}
            <Code>accountId</Code>).
          </Text>
          <Card className={classes.card} p="md" radius="md" mb="sm">
            <GqlBlock>
              {`# Self-service: remove a linked identity from the caller's own account
mutation DeleteMyIdentity($identityProviderId: ID!) {
  deleteAccountIdentity(identityProviderId: $identityProviderId)
  # Returns: Boolean!
}

# Admin: remove a linked identity from any account
mutation DeleteIdentity($identityProviderId: ID!, $accountId: ID) {
  deleteAccountIdentity(
    identityProviderId: $identityProviderId
    accountId: $accountId
  )
  # Returns: Boolean!
}`}
            </GqlBlock>
          </Card>
          <Alert
            icon={<IconAlertCircle />}
            color="yellow"
            variant="light"
            mb="md"
          >
            <Text size="sm" fw={600} mb={4}>
              Two identities can never be deleted:
            </Text>
            <List size="xs" spacing={4}>
              <List.Item>
                <strong>Creation identity</strong> — the oldest linked
                identity on the account. Deleting it would sever the account
                from the provider it was originally created with. Error code:{" "}
                <Code>IDENTITY_CREATION_IDENTITY_NOT_DELETABLE</Code>.
              </List.Item>
              <List.Item>
                <strong>Last identity</strong> — when only one identity
                remains, deleting it would make the account unreachable. Error
                code: <Code>IDENTITY_LAST_IDENTITY_NOT_DELETABLE</Code>.
              </List.Item>
            </List>
          </Alert>
          <Text size="sm" mb="xs">
            <strong>Self-service permission.</strong> The default public
            permission grant includes{" "}
            <Code>organization:account:identity:delete</Code> scoped to the
            caller's own account (resource pattern{" "}
            <Code>organization:account:{"{user.id}"}:identity:*</Code>), so
            authenticated users can unlink their own identities without any
            extra permission configuration.
          </Text>
          <Text size="sm" mb="sm">
            <strong>Admin permission.</strong> To delete another account's
            identity, the caller needs the{" "}
            <Code>organization:account:identity:delete</Code> permission with
            a resource matching{" "}
            <Code>
              organization:account:{"{accountId}"}:identity:{"{providerId}"}
            </Code>
            .
          </Text>

          <Title order={4} mb="xs">
            isCreationIdentity field{" "}
            <Badge color="teal" variant="light" size="sm" ml="xs">
              New
            </Badge>
          </Title>
          <Text size="sm" mb="sm">
            <Code>AccountIdentity</Code> now exposes an{" "}
            <Code>isCreationIdentity: Boolean</Code> field (nullable). When
            truthy, this identity was the one used to create the account and
            can never be deleted. The field is populated on the{" "}
            <Code>identities</Code> ResolveField (accessible via{" "}
            <Code>player.identities</Code> or the account's identity list);
            it is <Code>null</Code> on responses from{" "}
            <Code>accountIdentityRefresh</Code> and{" "}
            <Code>attachOrUpdateIdentityToAccount</Code>, which do not
            compute the creation flag.
          </Text>
          <Card className={classes.card} p="md" radius="md" mb="sm">
            <GqlBlock>
              {`query MyIdentities {
  getMyAccount {
    id
    identities {
      providerId
      organizationIdentityProviderId
      isCreationIdentity   # true if this is the oldest linked identity
      createdAt
      updatedAt
    }
  }
}`}
            </GqlBlock>
          </Card>

          <Title order={4} mt="lg" mb="xs">
            IDP URL configuration
          </Title>
          <Text size="sm" mb="sm">
            All URL fields on identity provider configurations (
            <Code>redirectUrl</Code>, <Code>authorizationUrl</Code>,{" "}
            <Code>tokenEndpoint</Code>, data-retriever URLs) require an{" "}
            <Code>http://</Code> or <Code>https://</Code> protocol prefix
            and now accept TLD-less hostnames such as{" "}
            <Code>http://localhost</Code>,{" "}
            <Code>http://127.0.0.1:8080</Code>, or internal domain names
            without a public TLD. The previous behaviour rejected these with
            a validation error.
          </Text>
        </div>

        {/* ===================== CONSENT & LOGOUT ===================== */}
        <div className={classes.section}>
          <SectionHeader
            icon={<IconListDetails size={18} />}
            title="Consent & Logout"
            id="consent-and-logout"
          />

          <Title order={4} mb="xs">
            Consent
          </Title>
          <Text mb="sm" size="sm">
            After a successful login, the platform decides whether to show a
            consent screen based on the <Code>requiresConsent</Code> flag on
            your Organization App:
          </Text>
          <List size="sm" spacing="xs" mb="md">
            <List.Item>
              <Code>requiresConsent: false</Code> (default for first-party
              apps) — consent is auto-granted with the requested scopes and
              audiences. The user is bounced straight to your{" "}
              <Code>redirect_uri</Code> with the authorization code.
            </List.Item>
            <List.Item>
              <Code>requiresConsent: true</Code> (typically third-party /
              marketplace apps) — the user is sent to the{" "}
              <Code>consentUrl</Code> configured on the app, where your
              consent UI calls <Code>POST /consent</Code> (accept) or{" "}
              <Code>DELETE /consent</Code> (reject). Use{" "}
              <Code>GET /consent/required?challenge=…</Code> to check
              upfront whether the screen needs to render at all.
            </List.Item>
          </List>
          <Alert
            icon={<IconInfoCircle />}
            color="indigo"
            variant="light"
            mb="md"
          >
            The platform automatically injects an{" "}
            <Code>organizationId</Code> claim into the issued access token so
            that downstream API calls are scoped to the right tenant. You do
            not need to do anything for this to work — just send the matching{" "}
            <Code>organization-id</Code> HTTP header on each GraphQL request.
          </Alert>

          <Title order={4} mb="xs">
            Logout
          </Title>
          <Text mb="sm" size="sm">
            Logout uses the standard OIDC end-session endpoint. The React SDK
            exposes <Code>useConnectedPlayer().logout()</Code> /{" "}
            <Code>useOidc().logout()</Code> which handles this for you:
          </Text>
          <Card className={classes.card} p="md" radius="md" mb="md">
            <GqlBlock>
              {`Browser → client logout → /oauth2/sessions/logout
       (with id_token_hint and post_logout_redirect_uri)
  → WellPlayed mints logout_challenge Q
  → 302 → logout UI?logout_challenge=Q
  → UI POST /logout { challenge: Q }
  → session is cleared
  → 302 → post_logout_redirect_uri`}
            </GqlBlock>
          </Card>
          <Text mb="sm" size="sm">
            The <Code>post_logout_redirect_uri</Code> must be registered on
            the Organization App (see <Code>logoutRedirectUrls</Code> below)
            or the request is rejected.
          </Text>
        </div>

        {/* ===================== ORGANIZATION APPS ===================== */}
        <div className={classes.section}>
          <SectionHeader
            icon={<IconSettings size={18} />}
            title="Organization Apps"
            id="apps"
          />
          <Text mb="md">
            Organization apps are OAuth2 clients that allow third-party
            applications to authenticate users within your organization. Each
            app has its own client ID, secret, and configuration for redirect
            URLs, consent, and login pages.
          </Text>

          <Title order={4} mb="xs">
            App Configuration Fields
          </Title>
          <Table
            striped
            highlightOnHover
            className={classes.statusTable}
            mb="md"
          >
            <thead>
              <tr>
                <th>Field</th>
                <th>Type</th>
                <th>Description</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>
                  <Code>name</Code>
                </td>
                <td>String!</td>
                <td>Display name for the application</td>
              </tr>
              <tr>
                <td>
                  <Code>description</Code>
                </td>
                <td>String!</td>
                <td>Description shown to users during consent</td>
              </tr>
              <tr>
                <td>
                  <Code>redirectUrls</Code>
                </td>
                <td>[String!]!</td>
                <td>Allowed redirect URIs after authentication</td>
              </tr>
              <tr>
                <td>
                  <Code>logoutRedirectUrls</Code>
                </td>
                <td>[String!]!</td>
                <td>Allowed redirect URIs after logout</td>
              </tr>
              <tr>
                <td>
                  <Code>loginUrl</Code>
                </td>
                <td>String!</td>
                <td>URL where users are redirected to log in</td>
              </tr>
              <tr>
                <td>
                  <Code>consentUrl</Code>
                </td>
                <td>String!</td>
                <td>URL for the consent screen</td>
              </tr>
              <tr>
                <td>
                  <Code>requiresConsent</Code>
                </td>
                <td>Boolean!</td>
                <td>
                  Whether to show the consent screen before granting access
                </td>
              </tr>
              <tr>
                <td>
                  <Code>public</Code>
                </td>
                <td>Boolean</td>
                <td>Whether the app is a public client (no secret required)</td>
              </tr>
              <tr>
                <td>
                  <Code>secret</Code>
                </td>
                <td>String</td>
                <td>Client secret for confidential clients</td>
              </tr>
            </tbody>
          </Table>

          <Accordion variant="separated" mb="md">
            <Accordion.Item value="attached-apps">
              <Accordion.Control>
                <Group gap="xs">
                  <IconBrackets size={16} />
                  <Text fw={500}>Attached Apps & Permissions</Text>
                </Group>
              </Accordion.Control>
              <Accordion.Panel>
                <Text size="sm" mb="xs">
                  Apps can be attached to permission groups within your
                  organization. When an app is attached to a group, API requests
                  made with that app&apos;s token inherit the permissions of
                  that group.
                </Text>
                <List size="sm" spacing="xs">
                  <List.Item>
                    Each attached app is linked to a specific permission group
                  </List.Item>
                  <List.Item>
                    The app inherits all permissions from the linked group
                  </List.Item>
                  <List.Item>
                    Multiple apps can be attached to the same group
                  </List.Item>
                </List>
              </Accordion.Panel>
            </Accordion.Item>

            <Accordion.Item value="secret-management">
              <Accordion.Control>
                <Group gap="xs">
                  <IconKey size={16} />
                  <Text fw={500}>Secret Management</Text>
                </Group>
              </Accordion.Control>
              <Accordion.Panel>
                <Text size="sm" mb="xs">
                  App secrets are used for authenticating confidential clients.
                  If a secret is compromised, you can regenerate it using the{" "}
                  <Code>resetOrganizationAppSecret</Code> mutation.
                </Text>
                <Alert
                  icon={<IconAlertCircle />}
                  color="yellow"
                  variant="light"
                  mt="xs"
                  p="xs"
                >
                  <Text size="xs">
                    Regenerating a secret immediately invalidates the previous
                    one. All applications using the old secret will need to be
                    updated.
                  </Text>
                </Alert>
              </Accordion.Panel>
            </Accordion.Item>
          </Accordion>
        </div>

        {/* ===================== WEBHOOKS ===================== */}
        <div className={classes.section}>
          <SectionHeader
            icon={<IconWebhook size={18} />}
            title="Webhooks"
            id="webhooks"
          />
          <Text mb="md">
            Webhooks send real-time HTTP POST notifications to your server when
            specific events occur in the system. Each webhook call includes a
            signed payload that you can verify for authenticity.
          </Text>

          <SimpleGrid cols={{ base: 1, md: 2 }} mb="md">
            <Card className={classes.card} p="md" radius="md">
              <Text fw={600} mb="xs">
                Webhook Headers
              </Text>
              <Text size="sm" mb="xs">
                Each webhook call includes the following headers:
              </Text>
              <List size="sm" spacing={4}>
                <List.Item>
                  <Code>WP-Webhook-Signature</Code> — HMAC SHA256 signature
                </List.Item>
                <List.Item>
                  <Code>WP-Webhook-Message-Id</Code> — Unique event ID
                </List.Item>
                <List.Item>
                  <Code>WP-Webhook-Timestamp</Code> — Event timestamp
                </List.Item>
                <List.Item>
                  <Code>WP-Webhook-Event</Code> — Event type that triggered the
                  call
                </List.Item>
              </List>
            </Card>
            <Card className={classes.card} p="md" radius="md">
              <Text fw={600} mb="xs">
                Webhook Configuration
              </Text>
              <List size="sm" spacing={4}>
                <List.Item>
                  <Code>url</Code> — The HTTP POST endpoint to call
                </List.Item>
                <List.Item>
                  <Code>events</Code> — Which event types to subscribe to
                </List.Item>
                <List.Item>
                  <Code>active</Code> — Enable or disable the webhook
                </List.Item>
                <List.Item>
                  <Code>secret</Code> — Auto-generated, used for signature
                  verification
                </List.Item>
              </List>
            </Card>
          </SimpleGrid>

          <Title order={4} mb="xs">
            Signature Verification
          </Title>
          <Text mb="sm">
            The signature is a HMAC SHA256 hash of the message ID, timestamp,
            and payload concatenated together, using the webhook secret as the
            key. Always verify signatures to ensure webhook calls are
            legitimate.
          </Text>
          <Card className={classes.card} p="md" radius="md" mb="md">
            <GqlBlock>
              {`import crypto from 'crypto';

const secret = 'your-webhook-secret';

// From request headers
const timestamp = req.headers['wp-webhook-timestamp'];
const messageId = req.headers['wp-webhook-message-id'];
const signature = req.headers['wp-webhook-signature'];

// From request body
const payload = JSON.stringify(req.body);

const expectedSignature = crypto
  .createHmac('sha256', secret)
  .update(messageId + timestamp + payload)
  .digest('hex');

const isValid = signature === expectedSignature;`}
            </GqlBlock>
          </Card>

          <Alert icon={<IconInfoCircle />} color="indigo" variant="light">
            Webhooks that include an <strong>Answer Payload</strong> are
            RPC-style webhooks that expect a response from your server. They do
            not have retries and are used for synchronous operations. Regular
            webhooks are fire-and-forget with automatic retries on failure.
          </Alert>

          <Text mt="md" size="sm" c="dimmed">
            A simple way to test webhook calls during development is to use a
            service like{" "}
            <Anchor href="https://webhook.site" target="_blank">
              webhook.site
            </Anchor>
            .
          </Text>
        </div>

        {/* ===================== WEBHOOK EVENTS ===================== */}
        <div className={classes.section}>
          <SectionHeader
            icon={<IconListDetails size={18} />}
            title="Available Webhook Events"
            id="webhook-events"
          />
          <Text mb="md">
            The following event types are available for webhook subscriptions.
            Each event has a specific payload structure that is delivered as the
            body of the HTTP POST request.
          </Text>

          <Accordion variant="separated">
            {data?.webhookAvailableEvents.map((event: any) => (
              <Accordion.Item key={event.type} value={event.type}>
                <Accordion.Control>
                  <Group gap="xs">
                    <Badge
                      color={event.answerPayload ? "orange" : "cyan"}
                      className={classes.mutationBadge}
                    >
                      {event.answerPayload ? "RPC" : "EVENT"}
                    </Badge>
                    <Text>{event.type}</Text>
                  </Group>
                </Accordion.Control>
                <Accordion.Panel>
                  <Stack gap="md">
                    <Text size="sm">{event.description}</Text>
                    <div>
                      <Text fw={600} mb="xs" size="sm">
                        Payload
                      </Text>
                      <GqlBlock>{payloads?.[event.type] ?? "{}"}</GqlBlock>
                      {event.payload?.properties &&
                        event.payload.properties.length > 0 && (
                          <Table
                            striped
                            className={classes.statusTable}
                            mt="sm"
                          >
                            <thead>
                              <tr>
                                <th>Field</th>
                                <th>Type</th>
                                <th>Description</th>
                              </tr>
                            </thead>
                            <tbody>
                              {event.payload.properties.map((property: any) => (
                                <tr key={property.name}>
                                  <td>
                                    <Code>{property.name}</Code>
                                  </td>
                                  <td>
                                    {property.type}
                                    {property.optional && " (optional)"}
                                  </td>
                                  <td>{property.description}</td>
                                </tr>
                              ))}
                            </tbody>
                          </Table>
                        )}
                    </div>
                    {event.answerPayload && (
                      <div>
                        <Text fw={600} mb="xs" size="sm">
                          Expected Response Payload
                        </Text>
                        {event.answerPayload.properties &&
                          event.answerPayload.properties.length > 0 && (
                            <Table striped className={classes.statusTable}>
                              <thead>
                                <tr>
                                  <th>Field</th>
                                  <th>Type</th>
                                  <th>Description</th>
                                </tr>
                              </thead>
                              <tbody>
                                {event.answerPayload.properties.map(
                                  (property: any) => (
                                    <tr key={property.name}>
                                      <td>
                                        <Code>{property.name}</Code>
                                      </td>
                                      <td>
                                        {property.type}
                                        {property.optional && " (optional)"}
                                      </td>
                                      <td>{property.description}</td>
                                    </tr>
                                  ),
                                )}
                              </tbody>
                            </Table>
                          )}
                      </div>
                    )}
                  </Stack>
                </Accordion.Panel>
              </Accordion.Item>
            ))}
          </Accordion>
        </div>

        {/* ===================== JOURNAL ===================== */}
        <div className={classes.section}>
          <SectionHeader
            icon={<IconDatabase size={18} />}
            title="Journal"
            id="journal"
          />
          <Text mb="md">
            The journal is the platform's append-only execution log. Every
            backend service that produces an externally interesting event —
            rule fires, tournament step generation/reset, manual admin
            overrides, validator simulations — writes a strongly-typed entry
            to the journal. Consumers read those entries through a single
            paginated query, fetch a single entry by id, or live-tail new
            entries through a GraphQL subscription. Entries are scoped to
            your organization, retained according to a per-category policy,
            and grouped into parent/child traces so a single rule fire and
            all its emitted effects can be inspected together.
          </Text>

          <Title order={4} mb="xs" id="journal-querying">
            Querying entries
          </Title>
          <Text mb="sm">
            <Code>journalEntries(page, filters)</Code> returns a cursor-paginated
            list of <Code>JournalEntry</Code> objects ordered most-recent
            first. <Code>journalEntry(id)</Code> looks up a single entry. Both
            queries require the <Code>organization:journal:view</Code>{" "}
            permission and are scoped to the active organization.
          </Text>

          <Card className={classes.card} p="md" radius="md" mb="md">
            <Text fw={600} mb="xs" size="sm">
              <Code>JournalFiltersInput</Code>
            </Text>
            <Table className={classes.statusTable}>
              <thead>
                <tr>
                  <th>Field</th>
                  <th>Type</th>
                  <th>Description</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>
                    <Code>categories</Code>
                  </td>
                  <td>
                    <Code>[JournalCategory!]</Code>
                  </td>
                  <td>Restrict to one or more categories.</td>
                </tr>
                <tr>
                  <td>
                    <Code>types</Code>
                  </td>
                  <td>
                    <Code>[String!]</Code>
                  </td>
                  <td>Exact namespaced entry types (e.g. <Code>rule_engine.advancement_rule.fired</Code>).</td>
                </tr>
                <tr>
                  <td>
                    <Code>typePrefix</Code>
                  </td>
                  <td>
                    <Code>String</Code>
                  </td>
                  <td>Prefix match (e.g. <Code>rule_engine.</Code>, <Code>tournaments.</Code>).</td>
                </tr>
                <tr>
                  <td>
                    <Code>actorType</Code>
                  </td>
                  <td>
                    <Code>JournalActorType</Code>
                  </td>
                  <td>Restrict to one actor type.</td>
                </tr>
                <tr>
                  <td>
                    <Code>actorId</Code>
                  </td>
                  <td>
                    <Code>ID</Code>
                  </td>
                  <td>Restrict to a single actor by id.</td>
                </tr>
                <tr>
                  <td>
                    <Code>primaryResourceType</Code>
                  </td>
                  <td>
                    <Code>String</Code>
                  </td>
                  <td>
                    Restrict to entries about a given resource type (e.g.{" "}
                    <Code>tournament_step</Code>).
                  </td>
                </tr>
                <tr>
                  <td>
                    <Code>primaryResourceId</Code>
                  </td>
                  <td>
                    <Code>ID</Code>
                  </td>
                  <td>Restrict to a single primary resource by id.</td>
                </tr>
                <tr>
                  <td>
                    <Code>severity</Code>
                  </td>
                  <td>
                    <Code>[JournalSeverity!]</Code>
                  </td>
                  <td>One or more severities.</td>
                </tr>
                <tr>
                  <td>
                    <Code>rootOnly</Code>
                  </td>
                  <td>
                    <Code>Boolean</Code>
                  </td>
                  <td>When true, only return entries with no parent.</td>
                </tr>
                <tr>
                  <td>
                    <Code>parentEntryId</Code>
                  </td>
                  <td>
                    <Code>ID</Code>
                  </td>
                  <td>Direct children of a specific entry.</td>
                </tr>
                <tr>
                  <td>
                    <Code>correlationId</Code>
                  </td>
                  <td>
                    <Code>ID</Code>
                  </td>
                  <td>Entries sharing the given correlation id.</td>
                </tr>
                <tr>
                  <td>
                    <Code>createdAfter</Code> / <Code>createdBefore</Code>
                  </td>
                  <td>
                    <Code>DateTime</Code>
                  </td>
                  <td>Inclusive time-range bounds.</td>
                </tr>
                <tr>
                  <td>
                    <Code>includeSimulations</Code>
                  </td>
                  <td>
                    <Code>Boolean</Code>
                  </td>
                  <td>
                    Defaults to <Code>false</Code>. When false, entries
                    produced by the <Code>SIMULATION</Code> actor (validator
                    dry-runs, sandboxed previews) are excluded server-side.
                  </td>
                </tr>
              </tbody>
            </Table>
          </Card>

          <Text mb="xs">
            Because <Code>JournalEntry.payload</Code> is a GraphQL interface,
            consumers must spread the concrete fragment they care about. The
            example below pulls the last 50 advancement-rule fires for a
            tournament step, with the typed payload fields exposed:
          </Text>
          <Card className={classes.card} p="md" radius="md" mb="md">
            <GqlBlock>
              {`query StepRuleFires($stepId: ID!) {
  journalEntries(
    page: { first: 50 }
    filters: {
      typePrefix: "rule_engine."
      primaryResourceType: "tournament_step"
      primaryResourceId: $stepId
    }
  ) {
    nodes {
      id
      type
      severity
      createdAt
      correlationId
      parentEntryId
      actor {
        type
        id
        context {
          ... on AdminActorContext { adminAccountId email }
          ... on SystemActorContext { source }
          ... on SimulationActorContext { simulationId reason }
        }
      }
      primaryResource { type id }
      secondaryResources { type id }
      payload {
        ... on RuleEngineAdvancementRuleFiredPayload {
          ruleId
          ruleName
          triggerType
          matched
        }
        ... on RuleEngineEffectAdvancePayload {
          teamId
          fromGameId
          toGameId
        }
        ... on RuleEngineEffectEliminatePayload {
          teamId
          reason
        }
      }
    }
    pageInfo { hasNextPage endCursor }
    totalCount
  }
}`}
            </GqlBlock>
          </Card>

          <Title order={4} mb="xs" id="journal-subscribing">
            Subscribing to new entries
          </Title>
          <Text mb="sm">
            <Code>journalEntryAdded(filters)</Code> is a GraphQL subscription
            that emits new <Code>JournalEntry</Code> rows as they are written.
            The same <Code>JournalFiltersInput</Code> shape is honored; the
            server filters every event before it leaves the box, so a
            narrowly-scoped subscription costs essentially nothing on the
            client.
          </Text>
          <Card className={classes.card} p="md" radius="md" mb="md">
            <GqlBlock>
              {`subscription LiveStepJournal($stepId: ID!) {
  journalEntryAdded(
    filters: {
      typePrefix: "rule_engine."
      primaryResourceType: "tournament_step"
      primaryResourceId: $stepId
    }
  ) {
    id
    type
    severity
    createdAt
    payload {
      ... on RuleEngineAdvancementRuleFiredPayload {
        ruleId
        matched
      }
    }
  }
}`}
            </GqlBlock>
          </Card>

          <Title order={4} mb="xs" id="journal-event-types">
            Entry types
          </Title>
          <Text mb="sm">
            Every entry's <Code>type</Code> is a stable, namespaced string. The
            payload returned by the <Code>JournalPayload</Code> interface is
            resolved to the concrete <Code>@ObjectType</Code> listed below.
          </Text>
          <Table striped className={classes.statusTable} mb="md">
            <thead>
              <tr>
                <th>Type</th>
                <th>Category</th>
                <th>Default severity</th>
                <th>Concrete payload type</th>
                <th>Triggered by</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>
                  <Code>rule_engine.advancement_rule.fired</Code>
                </td>
                <td>EXECUTION</td>
                <td>INFO</td>
                <td>
                  <Code>RuleEngineAdvancementRuleFiredPayload</Code>
                </td>
                <td>An advancement rule's condition evaluated and the action ran.</td>
              </tr>
              <tr>
                <td>
                  <Code>rule_engine.generation_script.ran</Code>
                </td>
                <td>EXECUTION</td>
                <td>INFO</td>
                <td>
                  <Code>RuleEngineGenerationScriptRanPayload</Code>
                </td>
                <td>A Lua <Code>GENERATION</Code> script executed at step creation.</td>
              </tr>
              <tr>
                <td>
                  <Code>rule_engine.cross_step.transferred</Code>
                </td>
                <td>EXECUTION</td>
                <td>INFO</td>
                <td>
                  <Code>RuleEngineCrossStepTransferredPayload</Code>
                </td>
                <td>A <Code>CROSS_STEP</Code> script moved teams/data between steps.</td>
              </tr>
              <tr>
                <td>
                  <Code>rule_engine.manual_override</Code>
                </td>
                <td>AUDIT</td>
                <td>INFO</td>
                <td>
                  <Code>RuleEngineManualOverridePayload</Code>
                </td>
                <td>An admin called <Code>setStepWinners</Code> / <Code>setTournamentWinners</Code> / <Code>setStepTeamStatus</Code>.</td>
              </tr>
              <tr>
                <td>
                  <Code>rule_engine.effect.advance</Code>
                </td>
                <td>EXECUTION</td>
                <td>INFO</td>
                <td>
                  <Code>RuleEngineEffectAdvancePayload</Code>
                </td>
                <td>An <Code>advance(team, target)</Code> Lua effect was applied.</td>
              </tr>
              <tr>
                <td>
                  <Code>rule_engine.effect.eliminate</Code>
                </td>
                <td>EXECUTION</td>
                <td>INFO</td>
                <td>
                  <Code>RuleEngineEffectEliminatePayload</Code>
                </td>
                <td>An <Code>eliminate(team)</Code> Lua effect was applied.</td>
              </tr>
              <tr>
                <td>
                  <Code>rule_engine.effect.set_metadata</Code>
                </td>
                <td>EXECUTION</td>
                <td>INFO</td>
                <td>
                  <Code>RuleEngineEffectSetMetadataPayload</Code>
                </td>
                <td>A <Code>set_metadata</Code> / <Code>set_custom_field</Code> Lua effect ran.</td>
              </tr>
              <tr>
                <td>
                  <Code>rule_engine.effect.end_step</Code>
                </td>
                <td>EXECUTION</td>
                <td>INFO</td>
                <td>
                  <Code>RuleEngineEffectEndStepPayload</Code>
                </td>
                <td>An <Code>end_step</Code> Lua effect closed the step.</td>
              </tr>
              <tr>
                <td>
                  <Code>rule_engine.effect.create_group</Code>
                </td>
                <td>EXECUTION</td>
                <td>INFO</td>
                <td>
                  <Code>RuleEngineEffectCreateGroupPayload</Code>
                </td>
                <td>A <Code>create_group</Code> Lua effect produced a new group.</td>
              </tr>
              <tr>
                <td>
                  <Code>rule_engine.effect.emit_seeding_pin</Code>
                </td>
                <td>EXECUTION</td>
                <td>INFO</td>
                <td>
                  <Code>RuleEngineEffectEmitSeedingPinPayload</Code>
                </td>
                <td>A seeding rule pinned a team to a specific slot.</td>
              </tr>
              <tr>
                <td>
                  <Code>tournaments.step.generated</Code>
                </td>
                <td>EXECUTION</td>
                <td>INFO</td>
                <td>
                  <Code>TournamentsStepGeneratedPayload</Code>
                </td>
                <td>A tournament step's groups/games were generated.</td>
              </tr>
              <tr>
                <td>
                  <Code>tournaments.step.reset</Code>
                </td>
                <td>AUDIT</td>
                <td>WARNING</td>
                <td>
                  <Code>TournamentsStepResetPayload</Code>
                </td>
                <td>An admin reset a tournament step (destructive).</td>
              </tr>
            </tbody>
          </Table>

          <Title order={4} mb="xs" id="journal-actor-types">
            Actor types
          </Title>
          <Text mb="sm">
            <Code>JournalActorType</Code> identifies who or what produced the
            entry. The <Code>JournalEntry.actor.context</Code> field is a
            GraphQL interface (<Code>JournalActorContextPayload</Code>) and
            resolves to the concrete <Code>@ObjectType</Code> listed below;
            select the fragment matching <Code>actorType</Code> in your
            query.
          </Text>
          <Table striped className={classes.statusTable} mb="md">
            <thead>
              <tr>
                <th>Actor type</th>
                <th>Concrete context type</th>
                <th>Meaning</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>
                  <Code>ADMIN</Code>
                </td>
                <td>
                  <Code>AdminActorContext</Code>
                </td>
                <td>An organization admin acting through the console or API.</td>
              </tr>
              <tr>
                <td>
                  <Code>PLAYER</Code>
                </td>
                <td>
                  <Code>PlayerActorContext</Code>
                </td>
                <td>An end-user player acting through a generated platform page.</td>
              </tr>
              <tr>
                <td>
                  <Code>SYSTEM</Code>
                </td>
                <td>
                  <Code>SystemActorContext</Code>
                </td>
                <td>An automated platform service (cron, queue worker, retry).</td>
              </tr>
              <tr>
                <td>
                  <Code>MARKETPLACE_MODULE</Code>
                </td>
                <td>
                  <Code>MarketplaceModuleActorContext</Code>
                </td>
                <td>An installed marketplace module hitting the API with an <Code>x-module-token</Code>.</td>
              </tr>
              <tr>
                <td>
                  <Code>API_CLIENT</Code>
                </td>
                <td>
                  <Code>ApiClientActorContext</Code>
                </td>
                <td>A direct API consumer authenticated with a Bearer token.</td>
              </tr>
              <tr>
                <td>
                  <Code>WEBHOOK_CALLBACK</Code>
                </td>
                <td>
                  <Code>WebhookCallbackActorContext</Code>
                </td>
                <td>An incoming RPC-style webhook answer altering platform state.</td>
              </tr>
              <tr>
                <td>
                  <Code>SIMULATION</Code>
                </td>
                <td>
                  <Code>SimulationActorContext</Code>
                </td>
                <td>
                  A validator dry-run or <Code>simulateScript</Code> sandboxed
                  preview. Excluded from queries by default — see below.
                </td>
              </tr>
            </tbody>
          </Table>

          <Title order={4} mb="xs" id="journal-permissions">
            Permissions and visibility
          </Title>
          <List spacing="xs" mb="md">
            <List.Item>
              <Code>organization:journal:view</Code>{" "}
              (<Code>ORGANIZATION_JOURNAL_VIEW</Code>) is required for both
              the <Code>journalEntries</Code> /{" "}
              <Code>journalEntry</Code> queries and the{" "}
              <Code>journalEntryAdded</Code> subscription.
            </List.Item>
            <List.Item>
              The permission is opt-in for marketplace modules — installed
              apps do not receive it by default and must be explicitly
              granted access during installation.
            </List.Item>
            <List.Item>
              Entries produced by the <Code>SIMULATION</Code> actor are
              excluded server-side unless the caller passes{" "}
              <Code>filters.includeSimulations: true</Code>. This keeps
              validator/preview noise out of operational dashboards.
            </List.Item>
          </List>

          <Title order={4} mb="xs" id="journal-retention">
            Retention
          </Title>
          <Text mb="sm">
            Each entry's <Code>category</Code> determines its default
            retention window. Background cleanup deletes root entries past
            the threshold; child entries are removed via cascade so traces
            stay coherent.
          </Text>
          <Table striped className={classes.statusTable} mb="md">
            <thead>
              <tr>
                <th>Category</th>
                <th>Default retention</th>
                <th>Intent</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>
                  <Code>EXECUTION</Code>
                </td>
                <td>30 days</td>
                <td>Rule fires and emitted effects — high-volume operational debugging.</td>
              </tr>
              <tr>
                <td>
                  <Code>AUDIT</Code>
                </td>
                <td>365 days</td>
                <td>Who-did-what trail (manual overrides, destructive admin actions).</td>
              </tr>
              <tr>
                <td>
                  <Code>SYSTEM</Code>
                </td>
                <td>90 days</td>
                <td>Platform-level events for trend analysis.</td>
              </tr>
              <tr>
                <td>
                  <Code>SIMULATION</Code>
                </td>
                <td>7 days</td>
                <td>Validator dry-runs and sandboxed previews.</td>
              </tr>
            </tbody>
          </Table>

          <Alert
            icon={<IconAlertCircle />}
            color="yellow"
            variant="light"
            mb="md"
          >
            <Text size="sm">
              The journal replaces the legacy <Code>logs(...)</Code> query
              and <Code>Log</Code> /{" "}
              <Code>LogType</Code> /{" "}
              <Code>LogAuthorType</Code> /{" "}
              <Code>LogData</Code> types, which have been removed. The
              <Code> ORGANIZATION_LOGS_VIEW</Code> permission is also gone.
              Migrate to <Code>journalEntries</Code> /{" "}
              <Code>journalEntry</Code> /{" "}
              <Code>journalEntryAdded</Code> and consume payloads through
              the typed <Code>JournalPayload</Code> interface. See the
              corresponding entry in{" "}
              {/*
                No canonical public URL for the deprecations changelog
                exists in the console codebase yet, so we surface the
                file path verbatim. Readers with backend access can find
                the full migration note at
                `api-warrior/apps/monolith/DEPRECATED.md`.
              */}
              <Code>api-warrior/apps/monolith/DEPRECATED.md</Code> (the
              API deprecations changelog) for the full migration note.
            </Text>
          </Alert>
        </div>

        {/* ===================== GRAPHQL API ===================== */}
        <div className={classes.section}>
          <SectionHeader
            icon={<IconCode size={18} />}
            title="GraphQL API Reference"
            id="graphql"
          />
          <Text mb="md">
            Complete list of all GraphQL operations available for the Apps &
            Webhooks system.
          </Text>

          <Accordion variant="separated">
            <Accordion.Item value="app-queries">
              <Accordion.Control>
                <Group gap="xs">
                  <Badge color="green" className={classes.mutationBadge}>
                    QUERY
                  </Badge>
                  <Text>App Queries</Text>
                </Group>
              </Accordion.Control>
              <Accordion.Panel>
                <Stack gap="md">
                  <div>
                    <Text fw={600} mb="xs">
                      organizationApp (detail)
                    </Text>
                    <GqlBlock>
                      {`query ($id: String!) {
  organizationApp(id: $id) {
    id, name, description, secret, public,
    organizationId, creatorId, createdAt, updatedAt,
    configuration {
      name, scope, redirectUrls, logoutRedirectUrls,
      metadata {
        consentUrl, loginUrl, public, requiresConsent, creatorId
      }
    }
  }
}`}
                    </GqlBlock>
                  </div>
                  <Divider />
                  <div>
                    <Text fw={600} mb="xs">
                      organizationApps (list)
                    </Text>
                    <GqlBlock>
                      {`query ($page: PageInfo!) {
  organizationApps(page: $page) {
    nodes { id, name, description, secret, public, createdAt }
    totalCount
    pageInfo { hasNextPage, endCursor }
  }
}`}
                    </GqlBlock>
                  </div>
                </Stack>
              </Accordion.Panel>
            </Accordion.Item>

            <Accordion.Item value="app-mutations">
              <Accordion.Control>
                <Group gap="xs">
                  <Badge color="red" className={classes.mutationBadge}>
                    MUTATION
                  </Badge>
                  <Text>App Mutations</Text>
                </Group>
              </Accordion.Control>
              <Accordion.Panel>
                <Stack gap="md">
                  <div>
                    <Text fw={600} mb="xs">
                      createOrganizationApp
                    </Text>
                    <GqlBlock>
                      {`mutation ($input: CreateOrganizationAppInput!) {
  createOrganizationApp(input: $input) {
    id, name, secret
  }
}

# CreateOrganizationAppInput:
# {
#   name: String!
#   description: String!
#   redirectUrls: [String!]!
#   logoutRedirectUrls: [String!]!
#   loginUrl: String!
#   consentUrl: String!
#   requiresConsent: Boolean!
#   public: Boolean
#   secret: String
# }`}
                    </GqlBlock>
                  </div>
                  <Divider />
                  <div>
                    <Text fw={600} mb="xs">
                      updateOrganizationApp
                    </Text>
                    <GqlBlock>
                      {`mutation ($id: String!, $input: UpdateOrganizationAppInput!) {
  updateOrganizationApp(id: $id, input: $input) {
    id, name
  }
}

# UpdateOrganizationAppInput:
# {
#   name: String
#   description: String
#   redirectUrls: [String!]!
#   logoutRedirectUrls: [String!]!
#   loginUrl: String!
#   consentUrl: String!
#   requiresConsent: Boolean!
#   secret: String
# }`}
                    </GqlBlock>
                  </div>
                  <Divider />
                  <div>
                    <Text fw={600} mb="xs">
                      deleteOrganizationApp
                    </Text>
                    <GqlBlock>
                      {`mutation ($id: String!) {
  deleteOrganizationApp(id: $id)
  # Returns: Boolean!
}`}
                    </GqlBlock>
                  </div>
                  <Divider />
                  <div>
                    <Text fw={600} mb="xs">
                      resetOrganizationAppSecret
                    </Text>
                    <Text size="sm" c="dimmed" mb="xs">
                      Regenerate the app secret. Invalidates the previous secret
                      immediately.
                    </Text>
                    <GqlBlock>
                      {`mutation ($id: String!) {
  resetOrganizationAppSecret(id: $id)
  # Returns: String! (the new secret)
}`}
                    </GqlBlock>
                  </div>
                </Stack>
              </Accordion.Panel>
            </Accordion.Item>

            <Accordion.Item value="webhook-queries">
              <Accordion.Control>
                <Group gap="xs">
                  <Badge color="green" className={classes.mutationBadge}>
                    QUERY
                  </Badge>
                  <Text>Webhook Queries</Text>
                </Group>
              </Accordion.Control>
              <Accordion.Panel>
                <Stack gap="md">
                  <div>
                    <Text fw={600} mb="xs">
                      webhook (detail)
                    </Text>
                    <GqlBlock>
                      {`query ($id: ID!) {
  webhook(id: $id) {
    id, url, description, active, secret,
    events, organizationId
  }
}`}
                    </GqlBlock>
                  </div>
                  <Divider />
                  <div>
                    <Text fw={600} mb="xs">
                      webhooks (list)
                    </Text>
                    <GqlBlock>
                      {`query {
  webhooks {
    id, url, description, active, secret, events
  }
}`}
                    </GqlBlock>
                  </div>
                  <Divider />
                  <div>
                    <Text fw={600} mb="xs">
                      webhookAvailableEvents
                    </Text>
                    <Text size="sm" c="dimmed" mb="xs">
                      Returns all available event types with their payload
                      schemas. Used to display event documentation and build
                      event selection UIs.
                    </Text>
                    <GqlBlock>
                      {`query {
  webhookAvailableEvents {
    type, description,
    payload {
      type, description, optional,
      properties { name, type, description, optional, array, example }
    },
    answerPayload {
      type, description, optional,
      properties { name, type, description, optional, array, example }
    }
  }
}`}
                    </GqlBlock>
                  </div>
                </Stack>
              </Accordion.Panel>
            </Accordion.Item>

            <Accordion.Item value="webhook-mutations">
              <Accordion.Control>
                <Group gap="xs">
                  <Badge color="red" className={classes.mutationBadge}>
                    MUTATION
                  </Badge>
                  <Text>Webhook Mutations</Text>
                </Group>
              </Accordion.Control>
              <Accordion.Panel>
                <Stack gap="md">
                  <div>
                    <Text fw={600} mb="xs">
                      createWebhook
                    </Text>
                    <GqlBlock>
                      {`mutation ($input: CreateWebhookInput!) {
  createWebhook(input: $input) {
    id, url, secret, active, events
  }
}

# CreateWebhookInput:
# {
#   url: String!
#   description: String!
#   events: [WebhookEventType!]!
#   active: Boolean!
# }`}
                    </GqlBlock>
                  </div>
                  <Divider />
                  <div>
                    <Text fw={600} mb="xs">
                      updateWebhook
                    </Text>
                    <GqlBlock>
                      {`mutation ($id: ID!, $input: UpdateWebhookInput!) {
  updateWebhook(id: $id, input: $input) {
    id, url, active, events
  }
}

# UpdateWebhookInput:
# {
#   url: String
#   description: String
#   events: [WebhookEventType!]
#   active: Boolean
# }`}
                    </GqlBlock>
                  </div>
                  <Divider />
                  <div>
                    <Text fw={600} mb="xs">
                      deleteWebhook
                    </Text>
                    <GqlBlock>
                      {`mutation ($id: ID!) {
  deleteWebhook(id: $id) {
    id
  }
}`}
                    </GqlBlock>
                  </div>
                  <Divider />
                  <div>
                    <Text fw={600} mb="xs">
                      regenerateWebhookSecret
                    </Text>
                    <Text size="sm" c="dimmed" mb="xs">
                      Generate a new signing secret for a webhook. Invalidates
                      the previous secret immediately.
                    </Text>
                    <GqlBlock>
                      {`mutation ($id: ID!) {
  regenerateWebhookSecret(id: $id) {
    id, secret
  }
}`}
                    </GqlBlock>
                  </div>
                </Stack>
              </Accordion.Panel>
            </Accordion.Item>

            <Accordion.Item value="input-types">
              <Accordion.Control>
                <Group gap="xs">
                  <Badge color="gray" className={classes.mutationBadge}>
                    INPUTS
                  </Badge>
                  <Text>Input Types Reference</Text>
                </Group>
              </Accordion.Control>
              <Accordion.Panel>
                <Stack gap="md">
                  <div>
                    <Text fw={600} mb="xs">
                      App Inputs
                    </Text>
                    <GqlBlock>
                      {`input CreateOrganizationAppInput {
  name: String!
  description: String!
  consentUrl: String!
  loginUrl: String!
  logoutRedirectUrls: [String!]!
  redirectUrls: [String!]!
  requiresConsent: Boolean!
  public: Boolean
  secret: String
}

input UpdateOrganizationAppInput {
  name: String
  description: String
  consentUrl: String!
  loginUrl: String!
  logoutRedirectUrls: [String!]!
  redirectUrls: [String!]!
  requiresConsent: Boolean!
  secret: String
}`}
                    </GqlBlock>
                  </div>
                  <Divider />
                  <div>
                    <Text fw={600} mb="xs">
                      Webhook Inputs
                    </Text>
                    <GqlBlock>
                      {`input CreateWebhookInput {
  url: String!
  description: String!
  events: [WebhookEventType!]!
  active: Boolean!
}

input UpdateWebhookInput {
  url: String
  description: String
  events: [WebhookEventType!]
  active: Boolean
}`}
                    </GqlBlock>
                  </div>
                </Stack>
              </Accordion.Panel>
            </Accordion.Item>
          </Accordion>
        </div>

        {/* ===================== DATA MODEL ===================== */}
        <div className={classes.section}>
          <SectionHeader
            icon={<IconDatabase size={18} />}
            title="Data Model"
            id="data-model"
          />
          <Text mb="md">
            Key types and enums used throughout the Apps & Webhooks module. All
            types are generated from the GraphQL schema via codegen.
          </Text>

          <Accordion variant="separated">
            <Accordion.Item value="enums">
              <Accordion.Control>Enums</Accordion.Control>
              <Accordion.Panel>
                <SimpleGrid cols={{ base: 1, md: 2 }}>
                  <div>
                    <Text fw={600} size="sm" mb="xs">
                      WebhookEventType
                    </Text>
                    <GqlBlock>
                      {`TOURNAMENT_TEAM_ATTENDANCE_UPDATED
TOURNAMENT_TEAM_CONFIRMATION_UPDATED
TOURNAMENT_TEAM_DELETED
TOURNAMENT_TEAM_DELETED_ADMIN
TOURNAMENT_TEAM_STATUS_UPDATED`}
                    </GqlBlock>
                  </div>
                  <div>
                    <Text fw={600} size="sm" mb="xs">
                      OauthEndpointAuthMethod
                    </Text>
                    <GqlBlock>
                      {`CLIENT_SECRET_BASIC
CLIENT_SECRET_POST`}
                    </GqlBlock>
                  </div>
                </SimpleGrid>
              </Accordion.Panel>
            </Accordion.Item>

            <Accordion.Item value="entities">
              <Accordion.Control>Entity Hierarchy</Accordion.Control>
              <Accordion.Panel>
                <GqlBlock>
                  {`OrganizationApp
├── id: String!
├── name: String!
├── description: String!
├── secret: String
├── public: Boolean
├── organizationId: ID!
├── creatorId: ID!
├── createdAt: DateTime!
├── updatedAt: DateTime!
└── configuration: OrganizationAppConfig
    ├── name: String!
    ├── scope: String!
    ├── redirectUrls: [String!]!
    ├── logoutRedirectUrls: [String!]!
    └── metadata: OrganizationAppConfigMetadata
        ├── consentUrl: String!
        ├── loginUrl: String!
        ├── public: Boolean!
        ├── requiresConsent: Boolean!
        └── creatorId: String!

OrganizationAttachedApp
├── appId: String!
├── groupId: ID!
├── organizationId: ID!
├── permissions: [GroupPermission]
├── app: OrganizationApp
├── group: OrganizationGroup
├── createdAt: DateTime!
└── updatedAt: DateTime!

Webhook
├── id: ID!
├── url: String!
├── description: String!
├── active: Boolean!
├── secret: String!
├── events: [WebhookEventType!]!
└── organizationId: ID!

WebhookEventConfiguration
├── type: WebhookEventType!
├── description: String!
├── payload: WebhookEventConfigurationProperties
│   ├── name: String!
│   ├── type: String!
│   ├── description: String
│   ├── optional: Boolean!
│   ├── array: Boolean!
│   ├── example: String
│   └── properties: [WebhookEventConfigurationProperties]
└── answerPayload: WebhookEventConfigurationProperties  (only for RPC events)`}
                </GqlBlock>
              </Accordion.Panel>
            </Accordion.Item>
          </Accordion>
        </div>

        {/* ===================== CUSTOM FIELDS & METADATA ===================== */}
        <div className={classes.section}>
          <SectionHeader
            icon={<IconDatabase size={18} />}
            title="Custom Fields & Metadata"
            id="custom-fields-metadata"
          />
          <Text mb="md">
            Webhooks support the platform-wide{" "}
            <Text fw={700} span>
              Custom Fields
            </Text>{" "}
            and{" "}
            <Text fw={700} span>
              Object Metadata
            </Text>{" "}
            systems from the Data service.
          </Text>
          <List spacing="xs" size="sm" mb="md">
            <List.Item>
              <Text fw={600} span>
                Custom Fields
              </Text>{" "}
              — Structured, typed fields defined in Data &gt; Custom Fields for
              the <Code>Webhook</Code> object type. Supports validation,
              visibility, and editability controls.
            </List.Item>
            <List.Item>
              <Text fw={600} span>
                Object Metadata
              </Text>{" "}
              — Flexible key-value store for attaching arbitrary data to
              webhooks. Each entry has a type (String, Boolean, URL, Image URL)
              and visibility (Public or Private).
            </List.Item>
          </List>
          <Text mb="sm">
            Manage these in the webhook detail screen (Custom Field Values and
            Metadata tabs) or via the GraphQL API:
          </Text>
          <GqlBlock>
            {`# Query custom field values for a webhook
query ($objectId: ID!) {
  customFieldValues(objectType: Webhook, objectId: $objectId) {
    definition { key, name, type, required }
    value
  }
}

# Query metadata for a webhook
query ($objectId: ID!) {
  objectMetadata(objectType: Webhook, objectId: $objectId) {
    key, value, type, visibility
  }
}`}
          </GqlBlock>
          <Alert icon={<IconInfoCircle />} color="cyan" variant="light" mt="md">
            See the{" "}
            <Anchor href="/data/documentations">Data Documentation</Anchor> for
            the full Custom Fields & Metadata API reference, including how to
            create definitions, set values, and manage metadata entries.
          </Alert>
        </div>
      </Stack>
    </div>
  );
};
